import React, {
	useState,
	useRef,
	useEffect,
	useMemo,
	useCallback,
	startTransition,
} from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
	format,
	parseISO,
	differenceInHours,
	differenceInMinutes,
} from "date-fns";
import { usePermissions } from "../hooks/usePermissions";
import { usePermissionPreview } from "../context/PermissionPreviewContext";
import NoPermissions from "./NoPermissions";
import { showError } from "./ErrorModal";
import ConfirmationModal from "./ConfirmationModal";
import CreateTimecardModal from "./CreateTimecardModal";
import {
	formatAppDate,
	formatAppTime,
	parseLocalDate,
	seattleWallToUtcDate,
	toAppDateString,
	toAppWallParts,
} from "../utils/dateUtils";
import {
	breakTypesForLocation,
	formatBreakPayStatus,
	formatBreakTypeLabel,
	splitBreakPayWindows,
	unpaidBreakMinutes,
} from "../utils/breakPay";
import "./TimeCards.css";

// A punch this far from its scheduled start/end is flagged as a time variance
const CLOCK_VARIANCE_MINUTES = 5;
// Leaving early is noise unless it's a big gap, so only flag long early-outs
const CLOCK_OUT_EARLY_FLAG_MINUTES = 120;
const TODAY_POLL_INTERVAL_MS = 2 * 60 * 1000;
const TIMECARD_REVIEW_SENDER_EMAIL = "kian@brinybagels.com";

function readStoredUserEmail() {
	try {
		const user = JSON.parse(localStorage.getItem("user") || "null");
		return String(user?.email || "")
			.trim()
			.toLowerCase();
	} catch {
		return "";
	}
}

function isValidReviewEmail(email) {
	const value = String(email || "").trim();
	if (!value || value.length > 254) return false;
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function normalizeShiftTime(time) {
	if (time == null || time === "") return "";
	const s = String(time);
	return s.length >= 5 ? s.slice(0, 5) : s;
}

function parseTimeToMinutes(time) {
	const normalized = normalizeShiftTime(time);
	if (!normalized) return null;
	const [h, m] = normalized.split(":").map(Number);
	if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
	return h * 60 + m;
}

/** Accepts either an "HH:mm" string or minutes-from-midnight. */
function formatScheduleClock(value) {
	const minutes =
		typeof value === "number" ? value : parseTimeToMinutes(value);
	if (minutes == null || !Number.isFinite(minutes)) return "";
	const normalized = ((minutes % 1440) + 1440) % 1440;
	const d = new Date();
	d.setHours(Math.floor(normalized / 60), normalized % 60, 0, 0);
	return format(d, "h:mm a");
}

function formatVarianceMinutes(minutes) {
	const total = Math.abs(Math.round(minutes));
	const hours = Math.floor(total / 60);
	const mins = total % 60;
	if (hours && mins) return `${hours}h ${mins}m`;
	if (hours) return `${hours}h`;
	return `${mins}m`;
}

function toLocalDateString(date = new Date()) {
	return toAppDateString(date);
}

/**
 * Format a YYYY-MM-DD workday as a civil calendar label (no timezone shift).
 * Avoids date-fns format() on UTC/Seattle instants, which can roll the day
 * in non-Pacific browser timezones.
 */
function formatWorkdayLabel(dateStr, pattern = "EEE, MMM d") {
	const raw = String(dateStr || "").slice(0, 10);
	const parsed = parseLocalDate(raw);
	if (!parsed) return raw || "";
	try {
		return format(parsed, pattern);
	} catch {
		return raw;
	}
}

/** Shift a Seattle YYYY-MM-DD by whole calendar days. */
function addAppDays(dateStr, delta) {
	const noon = seattleWallToUtcDate(String(dateStr).slice(0, 10), "12:00:00");
	return toAppDateString(
		new Date(noon.getTime() + delta * 24 * 60 * 60 * 1000),
	);
}

/**
 * Build a Seattle wall-clock instant. When end time is earlier than start on the
 * same calendar date, treat end as the next Seattle day (overnight punch).
 */
function seattleRangeFromDateAndTimes(dateStr, startTime, endTime) {
	const startAt = seattleWallToUtcDate(dateStr, startTime);
	let endAt = seattleWallToUtcDate(dateStr, endTime);
	if (endAt <= startAt) {
		endAt = seattleWallToUtcDate(addAppDays(dateStr, 1), endTime);
	}
	return { startAt, endAt };
}

/** Inclusive yyyy-MM-DD list for a selected range (Seattle calendar days). */
function eachDateInclusive(startDate, endDate) {
	if (!startDate || !endDate || startDate > endDate) return [];
	const dates = [];
	let cursor = String(startDate).slice(0, 10);
	const last = String(endDate).slice(0, 10);
	let guard = 0;
	while (cursor <= last && guard < 4000) {
		dates.push(cursor);
		const next = addAppDays(cursor, 1);
		if (next <= cursor) break;
		cursor = next;
		guard += 1;
	}
	return dates;
}

function getScheduleBounds(schedule) {
	const start = parseTimeToMinutes(schedule?.start_time);
	const end = parseTimeToMinutes(schedule?.end_time);
	if (start == null || end == null || end <= start) return null;
	return { start, end };
}

/** Unassigned coverage (published/draft) — no employee on the shift. */
function isOpenScheduleShift(schedule) {
	const id = schedule?.employee_id;
	return (
		id == null || id === "" || id === "open" || id === "null"
	);
}

/**
 * Break windows as percentages of a punch bar (for zebra overlays on the calendar).
 * Open breaks (started, no end) run through "now", clamped to the punch bounds.
 */
function getPunchBreakOverlays(timecard, bounds, nowMs) {
	if (!bounds || !(timecard?.breaks || []).length) return [];
	const barMs = bounds.end.getTime() - bounds.start.getTime();
	if (!(barMs > 0)) return [];

	const fallbackEnd = new Date(nowMs);
	return (timecard.breaks || []).flatMap((breakItem, idx) => {
		const name = breakItem.name || "Break";
		return splitBreakPayWindows(breakItem, fallbackEnd)
			.map((window, partIdx) => {
				const clampedStart = Math.max(
					window.start.getTime(),
					bounds.start.getTime(),
				);
				const clampedEnd = Math.min(
					window.end.getTime(),
					bounds.end.getTime(),
				);
				if (clampedEnd <= clampedStart) return null;
				const width = ((clampedEnd - clampedStart) / barMs) * 100;
				if (!(width > 0)) return null;
				const endsAtNow =
					!breakItem.endAt &&
					Math.abs(window.end.getTime() - fallbackEnd.getTime()) < 1000;
				return {
					key: `${breakItem.id || `break-${idx}`}-${window.isPaid ? "paid" : "unpaid"}-${partIdx}`,
					left: ((clampedStart - bounds.start.getTime()) / barMs) * 100,
					width,
					isPaid: window.isPaid,
					label: [
						name,
						`${formatAppTime(window.start)} – ${endsAtNow ? "now" : formatAppTime(window.end)}`,
						window.isPaid ? "paid" : "unpaid",
					].join(" · "),
				};
			})
			.filter(Boolean);
	});
}

/** Back-to-back shifts are one continuous expectation. */
function buildContiguousScheduleChains(daySchedules) {
	const entries = (daySchedules || [])
		.map((schedule) => {
			const bounds = getScheduleBounds(schedule);
			return bounds ? { schedule, ...bounds } : null;
		})
		.filter(Boolean)
		.sort((a, b) => a.start - b.start);

	const chains = [];
	entries.forEach((entry) => {
		const current = chains[chains.length - 1];
		if (current && entry.start === current.end) {
			current.end = entry.end;
			current.schedules.push(entry.schedule);
		} else {
			chains.push({
				start: entry.start,
				end: entry.end,
				schedules: [entry.schedule],
			});
		}
	});

	return chains.map((chain) => ({
		...chain,
		locationIds: Array.from(
			new Set(
				chain.schedules
					.map((s) =>
						s.location_id == null ? "" : String(s.location_id),
					)
					.filter(Boolean),
			),
		),
	}));
}

function memberDateKey(memberId, date) {
	return `${memberId || ""}|${date || ""}`;
}

function timecardDateString(timecard) {
	const workday = timecard?.workdayDate || timecard?.workday_date;
	if (workday) return String(workday).slice(0, 10);
	if (!timecard?.startAt) return null;
	try {
		return toAppDateString(parseISO(timecard.startAt));
	} catch {
		return null;
	}
}

function timecardIdKey(timecard) {
	if (timecard?.id == null || timecard.id === "") return "";
	return String(timecard.id);
}

/** Last write wins so the same Square punch is never counted or keyed twice. */
function uniqueTimecardsById(list) {
	const byId = new Map();
	const noId = [];
	(Array.isArray(list) ? list : []).forEach((tc) => {
		const id = timecardIdKey(tc);
		if (!id) {
			noId.push(tc);
			return;
		}
		byId.set(id, tc);
	});
	return [...byId.values(), ...noId];
}

/** ▲ alert that works on touch (tap) as well as hover title tooltips. */
function TimecardAlertMark({ className, message }) {
	const [open, setOpen] = useState(false);
	const [bubblePos, setBubblePos] = useState(null);
	const wrapRef = useRef(null);
	const buttonRef = useRef(null);

	const updateBubblePos = () => {
		const el = buttonRef.current;
		if (!el) return;
		const rect = el.getBoundingClientRect();
		setBubblePos({
			top: rect.top,
			left: rect.left + rect.width / 2,
		});
	};

	useEffect(() => {
		if (!open) return undefined;
		updateBubblePos();
		const onPointerDown = (event) => {
			if (wrapRef.current && !wrapRef.current.contains(event.target)) {
				const bubble = event.target.closest?.(".timecard-alert-bubble");
				if (!bubble) setOpen(false);
			}
		};
		const onKeyDown = (event) => {
			if (event.key === "Escape") setOpen(false);
		};
		const onReposition = () => updateBubblePos();
		const attachTimer = window.setTimeout(() => {
			window.addEventListener("pointerdown", onPointerDown);
			window.addEventListener("keydown", onKeyDown);
			window.addEventListener("scroll", onReposition, true);
			window.addEventListener("resize", onReposition);
		}, 0);
		const autoClose = window.setTimeout(() => setOpen(false), 5000);
		return () => {
			window.clearTimeout(attachTimer);
			window.clearTimeout(autoClose);
			window.removeEventListener("pointerdown", onPointerDown);
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("scroll", onReposition, true);
			window.removeEventListener("resize", onReposition);
		};
	}, [open]);

	if (!message) return null;

	return (
		<span
			ref={wrapRef}
			className={`timecard-alert-wrap ${className || ""}`.trim()}
		>
			<button
				ref={buttonRef}
				type="button"
				className="timecard-alert-mark"
				aria-label={message}
				aria-expanded={open}
				title={message}
				onClick={(event) => {
					event.stopPropagation();
					event.preventDefault();
					setOpen((prev) => !prev);
				}}
			>
				▲
			</button>
			{open &&
				bubblePos &&
				createPortal(
					<span
						className="timecard-alert-bubble"
						role="tooltip"
						style={{
							top: bubblePos.top,
							left: bubblePos.left,
						}}
					>
						{message}
					</span>,
					document.body,
				)}
		</span>
	);
}

function TimeCards() {
	const {
		hasPermission,
		hasSchedulePermission,
		hasAnySchedulePermission,
		rawPermissions,
	} = usePermissions();
	const { previewEmployeeId, previewOverridesEmployeeLink } =
		usePermissionPreview();
	const canViewShiftLeadDayMarks =
		hasPermission("view_shift_lead_day_marks") ||
		hasPermission("edit_shift_lead_day_marks") ||
		hasPermission("superuser");
	const canEditShiftLeadDayMarks =
		hasPermission("edit_shift_lead_day_marks") ||
		hasPermission("superuser");
	// Override ★ on calendar/table: load marks for any timecard viewer.
	const canLoadShiftLeadDayOverrides =
		canViewShiftLeadDayMarks ||
		hasPermission("view_timecards") ||
		hasPermission("view_timecards_as_shift_lead") ||
		hasPermission("edit_timecards") ||
		hasPermission("edit_timecards_as_shift_lead") ||
		hasPermission("superuser");
	const canViewTimecards =
		hasPermission("view_timecards") ||
		hasPermission("view_timecards_as_shift_lead") ||
		hasPermission("superuser");
	const canEditTimecards =
		hasPermission("edit_timecards") ||
		hasPermission("edit_timecards_as_shift_lead");
	// Match backend isShiftLeadScopedViewer: use granted perms only (not prereq expansion).
	// Otherwise e.g. view_estimated_tips → view_timecards makes the UI think access is unrestricted
	// and it ignores restrictedDates / skips shift-lead week handling.
	const isShiftLeadScoped =
		Array.isArray(rawPermissions) &&
		rawPermissions.includes("view_timecards_as_shift_lead") &&
		!rawPermissions.includes("view_timecards") &&
		!rawPermissions.includes("superuser");
	// Match backend assertShiftLeadTimecardAccess for edit: shift-lead editors without
	// full edit_timecards are always day/location scoped (even if they have full view).
	const needsShiftLeadEditScope =
		Array.isArray(rawPermissions) &&
		rawPermissions.includes("edit_timecards_as_shift_lead") &&
		!rawPermissions.includes("edit_timecards") &&
		!rawPermissions.includes("superuser");
	const canViewWages = hasPermission("view_wages");
	const canViewEstimatedTips = hasPermission("view_estimated_tips");
	const canViewRoles = hasPermission("view_roles");
	const canViewSchedules = hasAnySchedulePermission("view");
	const location = useLocation();
	const navigate = useNavigate();
	const [leadDayAssignments, setLeadDayAssignments] = useState([]);
	// When previewing a user, treat their employee link as "self" so own-timecard
	// create/edit/delete blocks match what they would see (preview is FE-only).
	const currentUserEmployeeId = useMemo(() => {
		if (previewOverridesEmployeeLink) {
			return previewEmployeeId == null ? null : Number(previewEmployeeId);
		}
		try {
			const user = JSON.parse(localStorage.getItem("user") || "null");
			if (user?.employee_id == null || user.employee_id === "")
				return null;
			return Number(user.employee_id);
		} catch {
			return null;
		}
	}, [previewOverridesEmployeeLink, previewEmployeeId]);
	// Session-only cache keyed by current user to avoid leaking data across logins
	const timecardsStorageKey = (() => {
		try {
			const user = JSON.parse(localStorage.getItem("user") || "null");
			return user?.id ? `timecards:${user.id}` : null;
		} catch {
			return null;
		}
	})();
	const schedulesStorageKey = (() => {
		try {
			const user = JSON.parse(localStorage.getItem("user") || "null");
			return user?.id ? `timecardSchedules:${user.id}` : null;
		} catch {
			return null;
		}
	})();
	const [timecards, setTimecards] = useState(() => {
		// Shift-lead scoped users must not hydrate a prior full roster from cache.
		try {
			const perms =
				JSON.parse(localStorage.getItem("user") || "null")
					?.permissions || [];
			const shiftLeadOnly =
				!perms.includes("view_timecards") &&
				!perms.includes("superuser") &&
				perms.includes("view_timecards_as_shift_lead");
			if (shiftLeadOnly) {
				if (timecardsStorageKey)
					localStorage.removeItem(timecardsStorageKey);
				return [];
			}
		} catch {
			// fall through to normal cache load
		}
		if (!timecardsStorageKey) return [];
		try {
			const saved = localStorage.getItem(timecardsStorageKey);
			if (saved) {
				const parsed = JSON.parse(saved);
				return uniqueTimecardsById(Array.isArray(parsed) ? parsed : []);
			}
		} catch (error) {
			console.error("Error loading timecards from localStorage:", error);
		}
		return [];
	});
	const [loading, setLoading] = useState(false);
	// Square sync indicator — does not hide the calendar/table.
	const [syncing, setSyncing] = useState(false);
	const [error, setError] = useState("");
	// Hydrate schedules so variance colors / dashed shifts paint on first frame (not after /api/schedules).
	const [schedules, setSchedules] = useState(() => {
		if (!schedulesStorageKey) return [];
		try {
			const saved = localStorage.getItem(schedulesStorageKey);
			if (!saved) return [];
			const parsed = JSON.parse(saved);
			return Array.isArray(parsed?.schedules) ? parsed.schedules : [];
		} catch (error) {
			console.error("Error loading schedules from localStorage:", error);
			return [];
		}
	});
	const [schedulesReady, setSchedulesReady] = useState(() => {
		if (!schedulesStorageKey) return false;
		try {
			const saved = localStorage.getItem(schedulesStorageKey);
			if (!saved) return false;
			const parsed = JSON.parse(saved);
			return (
				Array.isArray(parsed?.schedules) && parsed.schedules.length > 0
			);
		} catch {
			return false;
		}
	});
	// Days we've successfully loaded schedules for — only those may be flagged unscheduled
	const [scheduleDatesLoaded, setScheduleDatesLoaded] = useState(() => {
		if (!schedulesStorageKey) return new Set();
		try {
			const saved = localStorage.getItem(schedulesStorageKey);
			if (!saved) return new Set();
			const parsed = JSON.parse(saved);
			return new Set(
				Array.isArray(parsed?.datesLoaded) ? parsed.datesLoaded : [],
			);
		} catch {
			return new Set();
		}
	});
	const fetchInProgress = useRef(false);
	// Background refresh of today's punches must never collide with a full fetch
	const todayPollInProgress = useRef(false);
	const hasFullFetchRef = useRef(false);
	const initialFetchStartedRef = useRef(false);
	const scheduleDatesLoadedRef = useRef(scheduleDatesLoaded);
	scheduleDatesLoadedRef.current = scheduleDatesLoaded;
	// Tick once a minute so open "Now" bars don't rebuild every paint.
	const [nowMs, setNowMs] = useState(() => Date.now());
	useEffect(() => {
		const id = window.setInterval(() => setNowMs(Date.now()), 60_000);
		return () => window.clearInterval(id);
	}, []);
	// Holds the timecard's current job so we can select it after jobs finish loading
	const pendingEditJobRef = useRef(null);
	const editLeadDaySyncKeyRef = useRef("");
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [teamMembers, setTeamMembers] = useState([]);
	const [jobs, setJobs] = useState([]);
	const [breakTypes, setBreakTypes] = useState([]);
	const [locations, setLocations] = useState([]);
	const [locationFilter, setLocationFilter] = useState("");
	const [employeeFilter, setEmployeeFilter] = useState("");
	const [loadingFormData, setLoadingFormData] = useState(false);
	const [loadingJobs, setLoadingJobs] = useState(false);
	const localDateString = (date = new Date()) => toLocalDateString(date);

	const ownTeamMemberIds = useMemo(() => {
		const ids = new Set();
		if (currentUserEmployeeId == null) return ids;
		teamMembers.forEach((tm) => {
			if (
				tm.local_id != null &&
				Number(tm.local_id) === Number(currentUserEmployeeId)
			) {
				ids.add(String(tm.id));
			}
		});
		return ids;
	}, [teamMembers, currentUserEmployeeId]);

	const isOwnTimecard = useCallback(
		(timecard) => {
			if (!timecard || currentUserEmployeeId == null) return false;
			// Prefer local employee id from the list API so own-cards stay
			// blocked even before the create/edit roster is loaded.
			const localEmployeeId =
				timecard.localEmployeeId ?? timecard.local_employee_id;
			if (
				localEmployeeId != null &&
				Number(localEmployeeId) === Number(currentUserEmployeeId)
			) {
				return true;
			}
			const teamMemberId =
				timecard.teamMemberId || timecard.team_member_id;
			return (
				teamMemberId != null &&
				ownTeamMemberIds.has(String(teamMemberId))
			);
		},
		[ownTeamMemberIds, currentUserEmployeeId],
	);

	const isOwnTeamMemberId = useCallback(
		(teamMemberId) => {
			if (teamMemberId == null || teamMemberId === "") return false;
			if (ownTeamMemberIds.has(String(teamMemberId))) return true;
			if (currentUserEmployeeId == null) return false;
			const member = teamMembers.find(
				(tm) => String(tm.id) === String(teamMemberId),
			);
			return (
				member?.local_id != null &&
				Number(member.local_id) === Number(currentUserEmployeeId)
			);
		},
		[ownTeamMemberIds, currentUserEmployeeId, teamMembers],
	);

	const selectableTeamMembers = useMemo(
		() =>
			teamMembers.filter(
				(tm) =>
					currentUserEmployeeId == null ||
					tm.local_id == null ||
					Number(tm.local_id) !== Number(currentUserEmployeeId),
			),
		[teamMembers, currentUserEmployeeId],
	);

	const persistSchedulesCache = useCallback(
		(nextSchedules, datesLoadedSet) => {
			if (!schedulesStorageKey) return;
			try {
				localStorage.setItem(
					schedulesStorageKey,
					JSON.stringify({
						schedules: nextSchedules || [],
						datesLoaded: Array.from(datesLoadedSet || []),
					}),
				);
			} catch (error) {
				console.error("Error saving schedules to localStorage:", error);
			}
		},
		[schedulesStorageKey],
	);

	const markScheduleDatesLoaded = useCallback((rangeStart, rangeEnd) => {
		const dates = eachDateInclusive(rangeStart, rangeEnd);
		if (dates.length === 0) return;
		setScheduleDatesLoaded((prev) => {
			const next = new Set(prev);
			dates.forEach((date) => next.add(date));
			return next;
		});
	}, []);

	const mergeTimecardsForRange = useCallback(
		(prev, fetched, rangeStart, rangeEnd) => {
			const incoming = fetched || [];
			const fetchedIds = new Set(
				incoming.map(timecardIdKey).filter(Boolean),
			);
			const kept = prev.filter((tc) => {
				const id = timecardIdKey(tc);
				if (id && fetchedIds.has(id)) return false;
				const date = timecardDateString(tc);
				if (!date) return false;
				return date < rangeStart || date > rangeEnd;
			});
			return uniqueTimecardsById([...kept, ...incoming]);
		},
		[],
	);

	const mergeSchedulesForRange = useCallback(
		(prev, fetched, rangeStart, rangeEnd) => {
			const kept = prev.filter((schedule) => {
				const date = String(schedule.shift_date || "");
				return !date || date < rangeStart || date > rangeEnd;
			});
			return [...kept, ...(fetched || [])];
		},
		[],
	);

	const [deleteConfirmModal, setDeleteConfirmModal] = useState(null);
	const [deleting, setDeleting] = useState(false);
	const [isNarrow, setIsNarrow] = useState(() =>
		typeof window !== "undefined"
			? window.matchMedia("(max-width: 720px)").matches
			: false,
	);
	const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
	// Selected day for the phone calendar (day strip + member timelines).
	const [mobileCalendarDay, setMobileCalendarDay] = useState(null);
	// Desktop: calendar/table. Phone: list (card) by default; calendar optional.
	const [viewMode, setViewMode] = useState(() =>
		typeof window !== "undefined" &&
		window.matchMedia("(max-width: 720px)").matches
			? "list"
			: "calendar",
	);
	const [sortBy, setSortBy] = useState("startAt");
	const [sortOrder, setSortOrder] = useState("desc");
	const [selectedTimecard, setSelectedTimecard] = useState(null);
	const [showInfoModal, setShowInfoModal] = useState(false);
	const [showEditModal, setShowEditModal] = useState(false);
	const [canSendReviewEmail, setCanSendReviewEmail] = useState(
		() => readStoredUserEmail() === TIMECARD_REVIEW_SENDER_EMAIL,
	);
	const [showReviewEmailModal, setShowReviewEmailModal] = useState(false);
	const [reviewEmailTo, setReviewEmailTo] = useState("");
	const [reviewEmailSentTo, setReviewEmailSentTo] = useState("");
	const [sendingReviewEmail, setSendingReviewEmail] = useState(false);
	const [editFormData, setEditFormData] = useState({
		teamMemberId: "",
		locationId: "",
		jobId: "",
		date: "",
		clockIn: "",
		clockOut: "",
		breaks: [],
		tipEligible: true,
		is_shift_lead_day: false,
	});
	const locationBreakTypes = useMemo(
		() =>
			breakTypesForLocation(
				breakTypes,
				editFormData.locationId,
				(editFormData.breaks || []).map((b) => b.breakTypeId),
			),
		[breakTypes, editFormData.locationId, editFormData.breaks],
	);
	const [editSubmitting, setEditSubmitting] = useState(false);
	const [startDate, setStartDate] = useState(() => {
		// Default to Monday of current week (local calendar)
		const today = new Date();
		const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
		const monday = new Date(today);
		// Calculate days to subtract to get to Monday (if today is Sunday, go back 6 days; otherwise go back to Monday)
		const daysToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
		monday.setDate(today.getDate() - daysToMonday);
		return localDateString(monday);
	});
	const [endDate, setEndDate] = useState(() => {
		// Default to Sunday of current week (local calendar)
		const today = new Date();
		const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
		const sunday = new Date(today);
		// If today is Sunday, use today; otherwise calculate days to add to get to Sunday
		const daysToSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
		sunday.setDate(today.getDate() + daysToSunday);
		return localDateString(sunday);
	});
	const [shiftLeadAccess, setShiftLeadAccess] = useState(null);
	// Days with punches the shift-lead viewer cannot open (not scheduled as lead).
	const [restrictedDates, setRestrictedDates] = useState([]);

	useEffect(() => {
		const mq = window.matchMedia("(max-width: 720px)");
		let prevNarrow = mq.matches;
		setIsNarrow(prevNarrow);
		const onChange = () => {
			const narrow = mq.matches;
			if (narrow === prevNarrow) return;
			prevNarrow = narrow;
			setIsNarrow(narrow);
			setViewMode((mode) => {
				if (narrow) {
					// Table becomes list; calendar keeps the phone day-timeline view.
					return mode === "table" ? "list" : mode;
				}
				return mode === "list" ? "calendar" : mode;
			});
		};
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);

	const shiftLeadCacheClearedRef = useRef(false);
	useEffect(() => {
		if (!isShiftLeadScoped && !needsShiftLeadEditScope) {
			setShiftLeadAccess(null);
			shiftLeadCacheClearedRef.current = false;
			return undefined;
		}
		// Drop leftover full-roster cache once when entering scoped view mode (avoid
		// wiping punches again after a successful fetch if this effect re-runs).
		if (isShiftLeadScoped && !shiftLeadCacheClearedRef.current) {
			shiftLeadCacheClearedRef.current = true;
			try {
				if (timecardsStorageKey)
					localStorage.removeItem(timecardsStorageKey);
			} catch {
				// ignore
			}
			setTimecards([]);
			setRestrictedDates([]);
		}
		let cancelled = false;
		(async () => {
			try {
				const params = {};
				// Permission preview is FE-only; ask the API for the previewed
				// employee's lead days so client scoping matches that user.
				if (previewOverridesEmployeeLink && previewEmployeeId != null) {
					params.employeeId = previewEmployeeId;
				}
				const response = await axios.get(
					"/api/square/shift-lead-access",
					{ params },
				);
				if (cancelled) return;
				setShiftLeadAccess(response.data || null);
				const week = response.data?.allowedWeek;
				if (isShiftLeadScoped && week?.startDate && week?.endDate) {
					setStartDate((prev) =>
						prev < week.startDate ? week.startDate : prev,
					);
					setEndDate((prev) =>
						prev > week.endDate ? week.endDate : prev,
					);
				}
			} catch (error) {
				if (cancelled) return;
				console.error("Error loading shift lead access:", error);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [
		isShiftLeadScoped,
		needsShiftLeadEditScope,
		timecardsStorageKey,
		previewEmployeeId,
		previewOverridesEmployeeLink,
	]);

	const shiftLeadAllowedWeek = shiftLeadAccess?.allowedWeek || null;
	const shiftLeadDays = shiftLeadAccess?.days || {};

	const canEditTimecardScoped = useCallback(
		(timecard) => {
			if (!timecard) return false;
			// Nobody may edit their own punch — full editors and shift leads alike.
			if (isOwnTimecard(timecard)) return false;
			if (hasPermission("edit_timecards")) return true;
			if (!hasPermission("edit_timecards_as_shift_lead")) return false;
			const day = timecardDateString(timecard);
			const loc = timecard.locationId || timecard.location_id;
			if (!day || loc == null || loc === "") return false;
			const locs = shiftLeadDays[day];
			return Array.isArray(locs) && locs.includes(String(loc));
		},
		[hasPermission, shiftLeadDays, isOwnTimecard],
	);

	// Check for timecardId in URL params to open preview modal
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const timecardId = searchParams.get("timecardId");
		if (!timecardId) return undefined;

		const fromList = timecards.find((tc) => tc.id === timecardId);
		if (fromList) {
			setSelectedTimecard(fromList);
			setShowInfoModal(true);
			navigate("/time-cards", { replace: true });
			return undefined;
		}

		let cancelled = false;
		(async () => {
			try {
				const response = await axios.get(
					`/api/square/timecards/${timecardId}`,
				);
				if (cancelled || !response.data?.timecard) return;
				setSelectedTimecard(response.data.timecard);
				setShowInfoModal(true);
				navigate("/time-cards", { replace: true });
			} catch (error) {
				if (cancelled) return;
				showError(
					error.response?.data?.error ||
						"Could not open linked timecard",
				);
				navigate("/time-cards", { replace: true });
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [location.search, timecards, navigate]);

	useEffect(() => {
		const loadLocations = async () => {
			try {
				const response = await axios.get("/api/square/locations");
				const locs = response.data || [];
				setLocations(locs);
			} catch (error) {
				console.error("Error loading Square locations:", error);
			}
		};
		loadLocations();
	}, []);

	useEffect(() => {
		const applyEmail = (email) => {
			setCanSendReviewEmail(
				String(email || "").trim().toLowerCase() ===
					TIMECARD_REVIEW_SENDER_EMAIL,
			);
		};
		const stored = readStoredUserEmail();
		if (stored) {
			applyEmail(stored);
			return undefined;
		}
		let cancelled = false;
		axios
			.get("/api/auth/refresh-permissions")
			.then((response) => {
				if (cancelled) return;
				const email = response.data?.email || null;
				if (email) {
					try {
						const user =
							JSON.parse(localStorage.getItem("user") || "null") ||
							{};
						localStorage.setItem(
							"user",
							JSON.stringify({ ...user, email }),
						);
					} catch {
						// ignore persist failure
					}
				}
				applyEmail(email);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!showInfoModal) return;
		setCanSendReviewEmail(
			readStoredUserEmail() === TIMECARD_REVIEW_SENDER_EMAIL,
		);
	}, [showInfoModal]);

	useEffect(() => {
		if (!showReviewEmailModal || reviewEmailSentTo) return undefined;
		const id = window.setTimeout(() => {
			const input = document.querySelector(".timecard-review-email-input");
			if (input) input.focus();
		}, 0);
		return () => window.clearTimeout(id);
	}, [showReviewEmailModal, reviewEmailSentTo]);

	const loadTeamMembersAndBreakTypes = useCallback(
		async ({ force = false } = {}) => {
			if (!force && teamMembers.length > 0) return;
			try {
				setLoadingFormData(true);
				const response = await axios.get(
					"/api/square/team-members-jobs",
				);
				setTeamMembers(response.data.teamMembers || []);
				setBreakTypes(response.data.breakTypes || []);
				// Don't load jobs yet - wait for team member selection
				setJobs([]);
			} catch (error) {
				console.error("Error loading form data:", error);
				showError("Failed to load team members and break types");
			} finally {
				setLoadingFormData(false);
			}
		},
		[teamMembers.length],
	);

	// Prefetch roster (+ per-member jobs/locations) so create dropdowns unlock instantly.
	// Also load when opening create if the prefetch hasn't finished yet.
	useEffect(() => {
		if (!hasPermission("create_timecards")) return;
		loadTeamMembersAndBreakTypes();
	}, [hasPermission, loadTeamMembersAndBreakTypes]);

	useEffect(() => {
		if (showCreateModal && hasPermission("create_timecards")) {
			loadTeamMembersAndBreakTypes();
		}
	}, [showCreateModal, hasPermission, loadTeamMembersAndBreakTypes]);

	const leadDayRange = useMemo(() => {
		if (shiftLeadAllowedWeek?.startDate && shiftLeadAllowedWeek?.endDate) {
			return {
				start: shiftLeadAllowedWeek.startDate,
				end: shiftLeadAllowedWeek.endDate,
			};
		}
		return { start: startDate, end: endDate };
	}, [shiftLeadAllowedWeek, startDate, endDate]);

	const loadLeadDayAssignments = useCallback(async () => {
		if (!canLoadShiftLeadDayOverrides) return;
		if (!leadDayRange.start || !leadDayRange.end) return;
		try {
			const response = await axios.get(
				"/api/schedules/shift-lead-day-assignments",
				{
					params: {
						start: leadDayRange.start,
						end: leadDayRange.end,
					},
				},
			);
			setLeadDayAssignments(response.data || []);
		} catch (error) {
			console.error("Error loading shift-lead day marks:", error);
		}
	}, [canLoadShiftLeadDayOverrides, leadDayRange.start, leadDayRange.end]);

	const syncShiftLeadDayMark = useCallback(
		async ({ employeeId, date, locationId, assigned }) => {
			if (!canEditShiftLeadDayMarks) return;
			const id = Number(employeeId);
			const day = String(date || "").slice(0, 10);
			const loc = String(locationId || "").trim();
			if (!Number.isFinite(id) || id <= 0 || !day || !loc) return;
			await axios.put("/api/schedules/shift-lead-day-assignments", {
				employee_id: id,
				assignment_date: day,
				location_id: loc,
				assigned: !!assigned,
			});
			await loadLeadDayAssignments();
		},
		[canEditShiftLeadDayMarks, loadLeadDayAssignments],
	);

	const isMarkedShiftLeadDay = useCallback(
		(employeeId, date, locationId) => {
			const id = Number(employeeId);
			const day = String(date || "").slice(0, 10);
			const loc = String(locationId || "").trim();
			if (!Number.isFinite(id) || !day || !loc) return false;
			return (leadDayAssignments || []).some(
				(row) =>
					Number(row.employee_id) === id &&
					String(row.assignment_date || "").slice(0, 10) === day &&
					String(row.location_id || "").trim() === loc,
			);
		},
		[leadDayAssignments],
	);

	useEffect(() => {
		if (!canLoadShiftLeadDayOverrides) return;
		void loadLeadDayAssignments();
	}, [canLoadShiftLeadDayOverrides, loadLeadDayAssignments]);

	const getJobOptionValue = (job) => job?.id || job?.title || "";

	const resolveJobSelectValue = (jobList, preferredJobId, preferredTitle) => {
		if (!Array.isArray(jobList) || jobList.length === 0) {
			return preferredJobId || preferredTitle || "";
		}

		if (preferredJobId) {
			const byId = jobList.find(
				(job) => job.id && job.id === preferredJobId,
			);
			if (byId) return getJobOptionValue(byId);
		}

		if (preferredTitle) {
			const byTitle = jobList.find(
				(job) =>
					job.title &&
					job.title.toLowerCase() ===
						String(preferredTitle).toLowerCase(),
			);
			if (byTitle) return getJobOptionValue(byTitle);
		}

		// jobId may already be a title string from older data
		if (preferredJobId) {
			const byTitleAsId = jobList.find(
				(job) =>
					job.title &&
					job.title.toLowerCase() ===
						String(preferredJobId).toLowerCase(),
			);
			if (byTitleAsId) return getJobOptionValue(byTitleAsId);
		}

		return preferredJobId || preferredTitle || "";
	};

	const loadJobsForTeamMemberEdit = async (localId, options = {}) => {
		const { clearPending = false, preferred: preferredOverride = null } =
			options;
		try {
			setLoadingJobs(true);
			const response = await axios.get("/api/square/team-members-jobs", {
				params: { employeeId: localId },
			});
			let loadedJobs = response.data.jobs || [];
			const preferred =
				preferredOverride || pendingEditJobRef.current || {};
			const preferredJobId = preferred.jobId || null;
			const preferredTitle = preferred.title || null;

			// Keep the current assignment visible even if it isn't in the employee's role list
			const exists = loadedJobs.some(
				(job) =>
					(preferredJobId && job.id === preferredJobId) ||
					(preferredTitle &&
						job.title &&
						job.title.toLowerCase() ===
							String(preferredTitle).toLowerCase()) ||
					(preferredJobId &&
						job.title &&
						job.title.toLowerCase() ===
							String(preferredJobId).toLowerCase()),
			);
			if (!exists && (preferredJobId || preferredTitle)) {
				loadedJobs = [
					{
						id: preferredJobId || null,
						title: preferredTitle || preferredJobId,
						wage: preferred.wage || null,
						tipEligible:
							preferred.tipEligible !== undefined
								? preferred.tipEligible
								: true,
					},
					...loadedJobs,
				];
			}

			setJobs(loadedJobs);

			const resolvedJobId = resolveJobSelectValue(
				loadedJobs,
				preferredJobId,
				preferredTitle,
			);
			if (resolvedJobId) {
				setEditFormData((prev) => ({
					...prev,
					jobId: resolvedJobId,
					tipEligible:
						preferred.tipEligible !== undefined
							? preferred.tipEligible
							: prev.tipEligible,
				}));
			}

			if (clearPending || preferredOverride == null) {
				pendingEditJobRef.current = null;
			}
		} catch (error) {
			console.error("Error loading jobs for team member:", error);
			showError("Failed to load jobs for selected team member");
			setJobs([]);
		} finally {
			setLoadingJobs(false);
		}
	};

	// Load jobs when edit modal opens or edit team member changes
	useEffect(() => {
		if (
			!showEditModal ||
			!editFormData.teamMemberId ||
			teamMembers.length === 0
		) {
			return;
		}

		const selectedMember = teamMembers.find(
			(tm) => tm.id === editFormData.teamMemberId,
		);
		if (selectedMember && selectedMember.local_id) {
			loadJobsForTeamMemberEdit(selectedMember.local_id, {
				clearPending: true,
			});
		} else {
			setJobs([]);
		}
	}, [showEditModal, editFormData.teamMemberId, teamMembers]);

	// Prefill shift-lead day mark when employee/date/location identity changes.
	useEffect(() => {
		if (!showEditModal) {
			editLeadDaySyncKeyRef.current = "";
			return;
		}
		if (!canViewShiftLeadDayMarks) return;
		const member = teamMembers.find(
			(tm) => tm.id === editFormData.teamMemberId,
		);
		const localEmployeeId =
			member?.local_id ??
			selectedTimecard?.localEmployeeId ??
			selectedTimecard?.local_employee_id ??
			null;
		const key = `${localEmployeeId || ""}|${editFormData.date || ""}|${editFormData.locationId || ""}`;
		if (editLeadDaySyncKeyRef.current === key) return;
		editLeadDaySyncKeyRef.current = key;
		const marked = isMarkedShiftLeadDay(
			localEmployeeId,
			editFormData.date,
			editFormData.locationId,
		);
		setEditFormData((prev) =>
			prev.is_shift_lead_day === marked
				? prev
				: { ...prev, is_shift_lead_day: marked },
		);
	}, [
		showEditModal,
		canViewShiftLeadDayMarks,
		editFormData.teamMemberId,
		editFormData.date,
		editFormData.locationId,
		teamMembers,
		selectedTimecard,
		isMarkedShiftLeadDay,
	]);

	const handleDeleteClick = (timecard) => {
		if (isOwnTimecard(timecard)) {
			showError("You cannot delete your own timecard.");
			return;
		}
		setDeleteConfirmModal({
			timecard,
			message: `Are you sure you want to delete this timecard? This action cannot be undone.`,
		});
	};

	const closeReviewEmailModal = () => {
		if (sendingReviewEmail) return;
		setShowReviewEmailModal(false);
		setReviewEmailTo("");
		setReviewEmailSentTo("");
	};

	const handleSendReviewEmail = async () => {
		if (reviewEmailSentTo) {
			closeReviewEmailModal();
			return;
		}
		const to = reviewEmailTo.trim();
		if (!isValidReviewEmail(to)) {
			showError("Enter a valid email address");
			return;
		}
		if (!selectedTimecard?.id) {
			showError("No timecard selected");
			return;
		}
		setSendingReviewEmail(true);
		try {
			await axios.post(
				`/api/square/timecards/${encodeURIComponent(selectedTimecard.id)}/send-review-email`,
				{ to },
			);
			setReviewEmailSentTo(to);
		} catch (error) {
			showError(
				error.response?.data?.error || "Failed to send review email",
			);
		} finally {
			setSendingReviewEmail(false);
		}
	};

	const handleEditClick = (timecard) => {
		if (isOwnTimecard(timecard)) {
			showError("You cannot edit your own timecard.");
			return;
		}
		if (!canEditTimecardScoped(timecard)) {
			showError("You were not a shift lead that day.");
			return;
		}
		if (!timecard.startAt || !timecard.endAt) {
			showError("Cannot edit timecard: missing start or end time");
			return;
		}

		const startParts = toAppWallParts(timecard.startAt);
		const endParts = toAppWallParts(timecard.endAt);
		if (!startParts || !endParts) {
			showError("Cannot edit timecard: invalid start or end time");
			return;
		}

		// Remember current job so we can match dropdown options after jobs load
		// (option values may be local role titles even when the timecard has a Square job ID)
		pendingEditJobRef.current = {
			jobId: timecard.wage?.jobId || null,
			title: timecard.wage?.title || null,
			tipEligible:
				timecard.wage?.tipEligible !== undefined
					? timecard.wage.tipEligible
					: true,
			wage:
				timecard.wage?.hourlyRate?.amount != null
					? String(Number(timecard.wage.hourlyRate.amount) / 100)
					: null,
		};

		const member = teamMembers.find(
			(tm) => tm.id === (timecard.teamMemberId || ""),
		);
		const localEmployeeId =
			member?.local_id ??
			timecard.localEmployeeId ??
			timecard.local_employee_id ??
			null;
		setEditFormData({
			teamMemberId: timecard.teamMemberId || "",
			locationId: timecard.locationId || "",
			// Resolved against loaded job options after team-members-jobs returns
			jobId: "",
			date: startParts.date,
			clockIn: startParts.time,
			clockOut: endParts.time,
			tipEligible:
				timecard.wage?.tipEligible !== undefined
					? timecard.wage.tipEligible
					: true,
			is_shift_lead_day: isMarkedShiftLeadDay(
				localEmployeeId,
				startParts.date,
				timecard.locationId || "",
			),
			breaks:
				timecard.breaks && timecard.breaks.length > 0
					? timecard.breaks.map((b) => {
							const bStart = b.startAt
								? toAppWallParts(b.startAt)
								: null;
							const bEnd = b.endAt
								? toAppWallParts(b.endAt)
								: null;
							return {
								id: b.id || undefined,
								startAt: bStart?.time || "",
								endAt: bEnd?.time || "",
								breakTypeId: b.breakTypeId || "",
								name: b.name || "",
								isPaid:
									b.isPaid !== undefined ? b.isPaid : false,
								expectedDuration:
									b.expectedDuration ||
									b.expected_duration ||
									"",
							};
						})
					: [],
		});
		setSelectedTimecard(timecard);
		setShowInfoModal(false);
		setShowEditModal(true);
		loadTeamMembersAndBreakTypes();
	};

	const handleUpdateTimecard = async (e) => {
		e.preventDefault();

		if (
			isOwnTimecard(selectedTimecard) ||
			isOwnTeamMemberId(editFormData.teamMemberId)
		) {
			showError("You cannot edit your own timecard.");
			return;
		}

		if (
			!editFormData.teamMemberId ||
			!editFormData.locationId ||
			!editFormData.date ||
			!editFormData.clockIn ||
			!editFormData.clockOut
		) {
			showError("Please fill in all required fields");
			return;
		}

		for (const b of editFormData.breaks) {
			const hasAny = !!(b.breakTypeId || b.startAt || b.endAt);
			if (!hasAny) continue;
			if (!b.breakTypeId || !b.startAt || !b.endAt) {
				showError("Each break needs a type, start time, and end time");
				return;
			}
		}

		// Combine date with clock in/out times (Seattle wall clock; overnight → next day)
		const { startAt, endAt } = seattleRangeFromDateAndTimes(
			editFormData.date,
			editFormData.clockIn,
			editFormData.clockOut,
		);

		if (startAt >= endAt) {
			showError("Clock out time must be after clock in time");
			return;
		}

		for (const b of editFormData.breaks) {
			if (!b.breakTypeId || !b.startAt || !b.endAt) continue;
			const { startAt: breakStart, endAt: breakEnd } =
				seattleRangeFromDateAndTimes(
					editFormData.date,
					b.startAt,
					b.endAt,
				);
			if (breakStart >= breakEnd) {
				showError("Break end time must be after break start time");
				return;
			}
			if (breakStart < startAt || breakEnd > endAt) {
				showError("Breaks must fall within the clock-in/out times");
				return;
			}
		}

		try {
			setEditSubmitting(true);
			// Get job title from jobs list if jobId is selected
			const selectedJob = jobs.find(
				(j) => getJobOptionValue(j) === editFormData.jobId,
			);

			const finalJobTitle = selectedJob
				? selectedJob.title
				: editFormData.jobId || null;
			const finalJobWage =
				selectedJob && selectedJob.wage ? selectedJob.wage : null;

			const timecardData = {
				timecardId: selectedTimecard.id,
				teamMemberId: editFormData.teamMemberId,
				locationId: editFormData.locationId,
				jobId: selectedJob?.id || null,
				jobTitle: finalJobTitle,
				jobWage: finalJobWage,
				tipEligible:
					editFormData.tipEligible !== undefined
						? editFormData.tipEligible
						: true,
				startAt: startAt.toISOString(),
				endAt: endAt.toISOString(),
				breaks: editFormData.breaks
					.filter((b) => b.startAt && b.endAt && b.breakTypeId)
					.map((b) => {
						const range = seattleRangeFromDateAndTimes(
							editFormData.date,
							b.startAt,
							b.endAt,
						);
						return {
							id: b.id || undefined,
							startAt: range.startAt.toISOString(),
							endAt: range.endAt.toISOString(),
							breakTypeId: b.breakTypeId,
							name: b.name || "Break",
							isPaid: b.isPaid !== undefined ? b.isPaid : false,
							expectedDuration:
								b.expectedDuration || undefined,
						};
					}),
			};

			const response = await axios.put(
				`/api/square/timecards/${selectedTimecard.id}`,
				timecardData,
			);

			if (canEditShiftLeadDayMarks) {
				const member = teamMembers.find(
					(tm) => tm.id === editFormData.teamMemberId,
				);
				const employeeId =
					member?.local_id ??
					selectedTimecard?.localEmployeeId ??
					selectedTimecard?.local_employee_id ??
					null;
				const desiredMark = !!editFormData.is_shift_lead_day;
				const knownMark = isMarkedShiftLeadDay(
					employeeId,
					editFormData.date,
					editFormData.locationId,
				);
				if (employeeId != null && desiredMark !== knownMark) {
					try {
						await syncShiftLeadDayMark({
							employeeId,
							date: editFormData.date,
							locationId: editFormData.locationId,
							assigned: desiredMark,
						});
					} catch (markError) {
						console.error(
							"Error updating shift-lead day mark:",
							markError,
						);
						showError(
							markError.response?.data?.error ||
								"Timecard saved, but failed to update shift-lead day mark",
						);
					}
				}
			}

			// Get the updated timecard from the response
			let updatedTimecard = response.data?.timecard || response.data;

			// Find the original timecard to preserve any computed/enriched fields (like team member name)
			const originalTimecard = timecards.find(
				(tc) => tc.id === selectedTimecard.id,
			);

			// Normalize the updated timecard format
			if (updatedTimecard) {
				const normalizedUpdated = {
					id: updatedTimecard.id,
					startAt:
						updatedTimecard.startAt || updatedTimecard.start_at,
					endAt: updatedTimecard.endAt || updatedTimecard.end_at,
					teamMemberId:
						updatedTimecard.teamMemberId ||
						updatedTimecard.team_member_id,
					locationId:
						updatedTimecard.locationId ||
						updatedTimecard.location_id,
					locationName:
						updatedTimecard.locationName ||
						locations.find(
							(l) =>
								l.id ===
								(updatedTimecard.locationId ||
									updatedTimecard.location_id),
						)?.name ||
						null,
					timezone: updatedTimecard.timezone,
					wage: updatedTimecard.wage
						? {
								title: updatedTimecard.wage.title,
								hourlyRate: (() => {
									const rate =
										updatedTimecard.wage.hourlyRate ||
										updatedTimecard.wage.hourly_rate;
									if (!rate) return null;
									if (
										typeof rate === "object" &&
										rate.amount != null
									) {
										return {
											amount: rate.amount,
											currency: rate.currency || "USD",
										};
									}
									if (typeof rate === "number") {
										return {
											amount: Math.round(rate * 100),
											currency: "USD",
										};
									}
									return null;
								})(),
								jobId:
									updatedTimecard.wage.jobId ||
									updatedTimecard.wage.job_id,
								tipEligible:
									updatedTimecard.wage.tipEligible !==
									undefined
										? updatedTimecard.wage.tipEligible
										: updatedTimecard.wage.tip_eligible !==
											  undefined
											? updatedTimecard.wage.tip_eligible
											: true,
							}
						: null,
					breaks: (updatedTimecard.breaks || []).map((b) => ({
						startAt: b.startAt || b.start_at,
						endAt: b.endAt || b.end_at,
						breakTypeId: b.breakTypeId || b.break_type_id,
						name: b.name || "Break",
						isPaid:
							b.isPaid !== undefined
								? b.isPaid
								: b.is_paid !== undefined
									? b.is_paid
									: false,
						expectedDuration:
							b.expectedDuration || b.expected_duration,
					})),
					status: updatedTimecard.status,
					version: updatedTimecard.version,
					createdAt:
						updatedTimecard.createdAt || updatedTimecard.created_at,
					updatedAt:
						updatedTimecard.updatedAt || updatedTimecard.updated_at,
				};

				// Merge with original timecard to preserve all enriched fields (like teamMemberName)
				updatedTimecard = {
					...originalTimecard, // Start with original to preserve all fields
					...normalizedUpdated, // Override with updated values
				};

				// Update only this timecard in state
				const updatedTimecards = uniqueTimecardsById(
					timecards.map((tc) =>
						tc.id === updatedTimecard.id ? updatedTimecard : tc,
					),
				);
				setTimecards(updatedTimecards);

				// Update localStorage
				try {
					if (timecardsStorageKey) {
						localStorage.setItem(
							timecardsStorageKey,
							JSON.stringify(updatedTimecards),
						);
					}
				} catch (error) {
					console.error(
						"Error updating timecards in localStorage:",
						error,
					);
				}
			}

			setShowEditModal(false);
			setSelectedTimecard(null);
		} catch (error) {
			console.error("Error updating timecard:", error);
			const errorMessage =
				error.response?.data?.error || "Failed to update timecard";
			showError(errorMessage);
		} finally {
			setEditSubmitting(false);
		}
	};

	const confirmDeleteTimecard = async () => {
		if (!deleteConfirmModal || !deleteConfirmModal.timecard || deleting)
			return;
		if (isOwnTimecard(deleteConfirmModal.timecard)) {
			showError("You cannot delete your own timecard.");
			setDeleteConfirmModal(null);
			return;
		}

		const timecardId = deleteConfirmModal.timecard.id;

		try {
			setDeleting(true);
			await axios.delete(`/api/square/timecards/${timecardId}`);

			// Close overlays first so the confirm/details modals never look stuck
			setDeleteConfirmModal(null);
			setShowInfoModal(false);
			setSelectedTimecard(null);

			setTimecards((prev) => {
				const updatedTimecards = prev.filter(
					(tc) => tc.id !== timecardId,
				);
				try {
					if (timecardsStorageKey) {
						localStorage.setItem(
							timecardsStorageKey,
							JSON.stringify(updatedTimecards),
						);
					}
				} catch (error) {
					console.error(
						"Error updating timecards in localStorage:",
						error,
					);
				}
				return updatedTimecards;
			});
		} catch (error) {
			console.error("Error deleting timecard:", error);
			const errorMessage =
				error.response?.data?.error || "Failed to delete timecard";
			showError(errorMessage);
		} finally {
			setDeleting(false);
		}
	};

	const persistTimecards = useCallback(
		(nextTimecards) => {
			// Scoped viewers must not keep a durable cache of punches.
			if (isShiftLeadScoped) {
				try {
					if (timecardsStorageKey)
						localStorage.removeItem(timecardsStorageKey);
				} catch {
					// ignore
				}
				return;
			}
			try {
				if (timecardsStorageKey) {
					localStorage.setItem(
						timecardsStorageKey,
						JSON.stringify(uniqueTimecardsById(nextTimecards)),
					);
				}
			} catch (error) {
				console.error("Error saving timecards to localStorage:", error);
			}
		},
		[timecardsStorageKey, isShiftLeadScoped],
	);

	// Schedules are location-scoped; the API filters to locations the user can view.
	const fetchSchedulesForDates = useCallback(
		async (rangeStart, rangeEnd) => {
			if (!canViewSchedules || !rangeStart || !rangeEnd) return [];
			const params = { start: rangeStart, end: rangeEnd };

			if (locationFilter) {
				if (!hasSchedulePermission("view", locationFilter)) return [];
				const response = await axios.get("/api/schedules", {
					params: { ...params, location_id: locationFilter },
				});
				return response.data || [];
			}

			const response = await axios.get("/api/schedules", { params });
			return response.data || [];
		},
		[canViewSchedules, locationFilter, hasSchedulePermission],
	);

	const fetchSchedulesForRange = useCallback(
		() => fetchSchedulesForDates(startDate, endDate),
		[fetchSchedulesForDates, startDate, endDate],
	);

	// Schedules load with Fetch (same as timecards). Date tweaks only filter client-side
	// so changing start/end never empties punches or falsely marks them unscheduled.

	const applyTimecardsResponse = useCallback(
		(
			fetchedTimecards,
			rangeStart,
			rangeEnd,
			{ allowEmpty = true, restrictedDates: nextRestricted = null } = {},
		) => {
			const next = fetchedTimecards || [];
			// Background sync returning [] must not wipe punches already on screen.
			if (!allowEmpty && next.length === 0) return;
			setTimecards((prev) => {
				const mergedTimecards = mergeTimecardsForRange(
					prev,
					next,
					rangeStart,
					rangeEnd,
				);
				persistTimecards(mergedTimecards);
				return mergedTimecards;
			});
			if (Array.isArray(nextRestricted)) {
				setRestrictedDates((prev) => {
					const kept = (prev || []).filter(
						(d) => d < rangeStart || d > rangeEnd,
					);
					return Array.from(
						new Set([...kept, ...nextRestricted]),
					).sort();
				});
			} else if (!isShiftLeadScoped) {
				setRestrictedDates([]);
			}
			hasFullFetchRef.current = true;
		},
		[mergeTimecardsForRange, persistTimecards, isShiftLeadScoped],
	);

	const fetchFullRange = useCallback(
		async ({
			showErrors = false,
			recordAudit = false,
			showLoading = true,
			forceRefresh = false,
		} = {}) => {
			// Prevent multiple simultaneous fetches using ref (more reliable than state)
			if (fetchInProgress.current) {
				return;
			}

			if (!canViewTimecards) {
				if (showErrors)
					showError("You do not have permission to view timecards");
				return;
			}

			if (!startDate || !endDate) {
				if (showErrors)
					showError("Please select both start and end dates");
				return;
			}

			if (
				isShiftLeadScoped &&
				shiftLeadAllowedWeek?.startDate &&
				startDate < shiftLeadAllowedWeek.startDate
			) {
				const msg =
					"Date is too far in the past. Shift leads can only view/edit timecards for the past and current week.";
				if (showErrors) showError(msg);
				setError(msg);
				return;
			}

			if (new Date(startDate) > new Date(endDate)) {
				if (showErrors)
					showError("Start date must be before or equal to end date");
				return;
			}

			try {
				fetchInProgress.current = true;
				// Only block the body when we have nothing to show yet.
				const hasExisting =
					timecards.length > 0 || schedules.length > 0;
				if (showLoading && !hasExisting) setLoading(true);
				// Fetch button "Loading…" only for explicit sync — not background refresh.
				if (recordAudit || forceRefresh) setSyncing(true);
				setError("");

				const baseParams = {
					startDate,
					endDate,
					...(locationFilter ? { locationId: locationFilter } : {}),
				};

				const loadSchedulesInBackground = () => {
					if (!canViewSchedules) return;
					fetchSchedulesForRange()
						.then((loadedSchedules) => {
							setSchedules((prev) => {
								const merged = mergeSchedulesForRange(
									prev,
									loadedSchedules,
									startDate,
									endDate,
								);
								const dates = new Set(scheduleDatesLoaded);
								eachDateInclusive(startDate, endDate).forEach(
									(d) => dates.add(d),
								);
								persistSchedulesCache(merged, dates);
								return merged;
							});
							markScheduleDatesLoaded(startDate, endDate);
							setSchedulesReady(true);
						})
						.catch((scheduleError) => {
							console.error(
								"Error fetching schedules:",
								scheduleError,
							);
						});
				};

				// Explicit Fetch button: wait on Square. Otherwise always local-first + background sync.
				if (recordAudit || forceRefresh) {
					loadSchedulesInBackground();
					const response = await axios.get("/api/square/timecards", {
						params: {
							...baseParams,
							refresh: 1,
							...(recordAudit ? { recordAudit: 1 } : {}),
						},
					});
					applyTimecardsResponse(
						response.data.timecards || [],
						startDate,
						endDate,
						{
							allowEmpty: true,
							restrictedDates: Array.isArray(
								response.data.restrictedDates,
							)
								? response.data.restrictedDates
								: null,
						},
					);
				} else {
					// Warm schedule cache: fetch punches alone first (avoid HTTP contention with schedules).
					// Cold: start schedules in parallel so colors aren't delayed.
					const hadCachedSchedules = schedules.length > 0;
					const localPromise = axios.get("/api/square/timecards", {
						params: baseParams,
					});
					if (!hadCachedSchedules) loadSchedulesInBackground();
					const localResponse = await localPromise;
					const localCards = localResponse.data.timecards || [];
					// Shift-lead scope must always accept an empty authoritative response
					// (otherwise a prior full-roster cache would keep showing every card).
					const allowEmpty =
						isShiftLeadScoped ||
						localCards.length > 0 ||
						timecards.length === 0;
					applyTimecardsResponse(localCards, startDate, endDate, {
						allowEmpty,
						restrictedDates: Array.isArray(
							localResponse.data.restrictedDates,
						)
							? localResponse.data.restrictedDates
							: null,
					});
					setLoading(false);
					fetchInProgress.current = false;
					if (hadCachedSchedules) loadSchedulesInBackground();

					// Always quiet-sync from Square after local paint so closed punches /
					// edits aren't stuck showing as open "now" until Fetch is clicked.
					axios
						.get("/api/square/timecards", {
							params: { ...baseParams, refresh: 1 },
						})
						.then((refreshResponse) => {
							startTransition(() => {
								applyTimecardsResponse(
									refreshResponse.data.timecards || [],
									startDate,
									endDate,
									{
										allowEmpty: true,
										restrictedDates: Array.isArray(
											refreshResponse.data
												.restrictedDates,
										)
											? refreshResponse.data
													.restrictedDates
											: null,
									},
								);
							});
						})
						.catch((refreshError) => {
							console.error(
								"Background timecard sync failed:",
								refreshError,
							);
						});
				}
			} catch (error) {
				console.error("Error fetching timecards:", error);
				const errorMessage =
					error.response?.data?.error || "Failed to fetch timecards";
				setError(errorMessage);
				if (showErrors) showError(errorMessage);
			} finally {
				setLoading(false);
				setSyncing(false);
				fetchInProgress.current = false;
			}
		},
		[
			canViewTimecards,
			isShiftLeadScoped,
			shiftLeadAllowedWeek,
			startDate,
			endDate,
			locationFilter,
			applyTimecardsResponse,
			canViewSchedules,
			fetchSchedulesForRange,
			mergeSchedulesForRange,
			markScheduleDatesLoaded,
			persistSchedulesCache,
			scheduleDatesLoaded,
			timecards.length,
			schedules.length,
		],
	);

	const handleFetch = useCallback(
		() => fetchFullRange({ showErrors: true, recordAudit: true }),
		[fetchFullRange],
	);

	// After create, refresh in place — don't blank the calendar or write a sync audit
	const refreshAfterCreate = useCallback(
		() =>
			fetchFullRange({
				showErrors: false,
				recordAudit: false,
				showLoading: false,
				forceRefresh: true,
			}),
		[fetchFullRange],
	);

	const closeCreateModal = useCallback(() => setShowCreateModal(false), []);

	// Load the default range once so the page isn't empty on arrival
	useEffect(() => {
		if (initialFetchStartedRef.current) return;
		if (!canViewTimecards) return;
		if (!startDate || !endDate) return;
		// Wait for shift-lead week bounds before the first fetch so we don't 403.
		if (isShiftLeadScoped && !shiftLeadAccess) return;
		initialFetchStartedRef.current = true;
		fetchFullRange({ showErrors: false });
	}, [
		fetchFullRange,
		canViewTimecards,
		isShiftLeadScoped,
		shiftLeadAccess,
		startDate,
		endDate,
	]);

	// Open shifts change all day, so today's slice is re-pulled quietly in the background
	const refreshTodaysTimecards = useCallback(async () => {
		if (!hasFullFetchRef.current) return;
		if (todayPollInProgress.current || fetchInProgress.current) return;
		if (!canViewTimecards) return;

		const today = localDateString();
		if (!startDate || !endDate || today < startDate || today > endDate)
			return;

		try {
			todayPollInProgress.current = true;
			const timecardsPromise = axios
				.get("/api/square/timecards", {
					params: {
						startDate: today,
						endDate: today,
						refresh: 1,
						...(locationFilter
							? { locationId: locationFilter }
							: {}),
					},
				})
				.then((response) => {
					const todaysTimecards = response.data.timecards || [];
					startTransition(() => {
						applyTimecardsResponse(todaysTimecards, today, today, {
							allowEmpty: true,
							restrictedDates: Array.isArray(
								response.data.restrictedDates,
							)
								? response.data.restrictedDates
								: null,
						});
					});
					return todaysTimecards;
				});

			const schedulesPromise = canViewSchedules
				? fetchSchedulesForDates(today, today)
						.then((todaysSchedules) => {
							setSchedules((prev) => {
								const merged = mergeSchedulesForRange(
									prev,
									todaysSchedules,
									today,
									today,
								);
								const dates = new Set(
									scheduleDatesLoadedRef.current,
								);
								dates.add(today);
								persistSchedulesCache(merged, dates);
								return merged;
							});
							markScheduleDatesLoaded(today, today);
							setSchedulesReady(true);
							return todaysSchedules;
						})
						.catch((scheduleError) => {
							console.error(
								"Error refreshing today's schedules:",
								scheduleError,
							);
							return null;
						})
				: Promise.resolve(null);

			await Promise.all([timecardsPromise, schedulesPromise]);
		} catch (error) {
			console.error("Error refreshing today's timecards:", error);
		} finally {
			todayPollInProgress.current = false;
		}
	}, [
		canViewTimecards,
		startDate,
		endDate,
		locationFilter,
		applyTimecardsResponse,
		canViewSchedules,
		fetchSchedulesForDates,
		mergeSchedulesForRange,
		markScheduleDatesLoaded,
		persistSchedulesCache,
	]);

	useEffect(() => {
		if (!canViewTimecards) return undefined;

		// Run once the initial local fetch has landed (or shortly after).
		const kickoff = window.setTimeout(() => {
			refreshTodaysTimecards();
		}, 750);

		const interval = setInterval(() => {
			refreshTodaysTimecards();
		}, TODAY_POLL_INTERVAL_MS);

		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				refreshTodaysTimecards();
			}
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);

		return () => {
			window.clearTimeout(kickoff);
			clearInterval(interval);
			document.removeEventListener(
				"visibilitychange",
				handleVisibilityChange,
			);
		};
	}, [canViewTimecards, refreshTodaysTimecards]);

	const formatDuration = (startAt, endAt) => {
		if (!startAt || !endAt) return "N/A";
		try {
			const start = parseISO(startAt);
			const end = parseISO(endAt);
			const totalMinutes = differenceInMinutes(end, start);
			const hours = Math.floor(totalMinutes / 60);
			const minutes = totalMinutes % 60;
			return `${hours}h ${minutes}m`;
		} catch (e) {
			return "N/A";
		}
	};

	const getDurationMinutes = (timecard) => {
		if (!timecard?.startAt) return -1;
		try {
			const start = parseISO(timecard.startAt);
			const end =
				timecard.status === "OPEN" && !timecard.endAt
					? new Date(nowMs)
					: timecard.endAt
						? parseISO(timecard.endAt)
						: null;
			if (!end || isNaN(start.getTime()) || isNaN(end.getTime()))
				return -1;
			return differenceInMinutes(end, start);
		} catch (e) {
			return -1;
		}
	};

	// Paid/worked minutes after unpaid breaks (including paid rest-break overage)
	const getWorkedMinutes = (timecard) => {
		const minutes = getDurationMinutes(timecard);
		if (minutes < 0) return null;
		let unpaid = 0;
		(timecard.breaks || []).forEach((breakItem) => {
			unpaid += unpaidBreakMinutes(breakItem);
		});
		return Math.max(0, minutes - unpaid);
	};

	const getLaborCostCents = (timecard) => {
		const worked = getWorkedMinutes(timecard);
		const rate = timecard?.wage?.hourlyRate?.amount;
		if (worked == null || rate == null || !Number.isFinite(rate))
			return null;
		return Math.round((rate * worked) / 60);
	};

	const formatHoursTotal = (hours) => {
		if (hours == null || !Number.isFinite(hours)) return "";
		const totalMinutes = Math.round(hours * 60);
		const h = Math.floor(totalMinutes / 60);
		const m = totalMinutes % 60;
		if (h && m) return `${h}h ${m}m`;
		if (h) return `${h}h`;
		return `${m}m`;
	};

	const handleSort = (column) => {
		if (sortBy === column) {
			setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortBy(column);
			setSortOrder(
				column === "startAt" || column === "endAt" || column === "date"
					? "desc"
					: "asc",
			);
		}
	};

	const getSortValue = (timecard, column) => {
		switch (column) {
			case "date":
			case "startAt":
				return timecard.startAt
					? new Date(timecard.startAt).getTime()
					: 0;
			case "endAt":
				if (timecard.status === "OPEN" && !timecard.endAt)
					return Number.MAX_SAFE_INTEGER;
				return timecard.endAt ? new Date(timecard.endAt).getTime() : 0;
			case "duration":
				return getDurationMinutes(timecard);
			case "teamMember":
				return (
					timecard.teamMemberName ||
					timecard.teamMemberId ||
					""
				).toLowerCase();
			case "location":
				return (
					timecard.locationName ||
					locations.find((l) => l.id === timecard.locationId)?.name ||
					timecard.locationId ||
					""
				).toLowerCase();
			case "jobTitle":
				return (timecard.wage?.title || "").toLowerCase();
			case "hourlyRate":
				return timecard.wage?.hourlyRate?.amount ?? -1;
			case "laborCost":
				return getLaborCostCents(timecard) ?? -1;
			case "estimatedTips":
				return timecard.estimatedTips?.amount ?? -1;
			case "status":
				return (timecard.status || "").toLowerCase();
			case "breaks":
				return timecard.breaks?.length || 0;
			default:
				return "";
		}
	};

	// Date/location/employee narrowing is client-side so tweaking filters never refetches
	const filteredTimecards = useMemo(() => {
		return uniqueTimecardsById(timecards).filter((tc) => {
			const date = timecardDateString(tc);
			if (startDate && date && date < startDate) return false;
			if (endDate && date && date > endDate) return false;
			if (
				locationFilter &&
				String(tc.locationId || "") !== String(locationFilter)
			)
				return false;
			if (
				employeeFilter &&
				String(tc.teamMemberId || "") !== String(employeeFilter)
			)
				return false;
			// Enforce lead days/locations on the client too: required for permission
			// preview (API still uses the real admin JWT) and defense in depth.
			if (isShiftLeadScoped) {
				if (!date) return false;
				if (
					shiftLeadAllowedWeek?.startDate &&
					date < shiftLeadAllowedWeek.startDate
				) {
					return false;
				}
				if (
					shiftLeadAllowedWeek?.endDate &&
					date > shiftLeadAllowedWeek.endDate
				) {
					return false;
				}
				const locs = shiftLeadDays[date];
				const loc = tc.locationId || tc.location_id;
				if (!Array.isArray(locs) || locs.length === 0) return false;
				if (loc == null || loc === "" || !locs.includes(String(loc)))
					return false;
			}
			return true;
		});
	}, [
		timecards,
		startDate,
		endDate,
		locationFilter,
		employeeFilter,
		isShiftLeadScoped,
		shiftLeadAllowedWeek,
		shiftLeadDays,
	]);

	const filteredSchedules = useMemo(() => {
		return schedules.filter((s) => {
			if (isOpenScheduleShift(s)) return false;
			const date = String(s.shift_date || "");
			if (startDate && date && date < startDate) return false;
			if (endDate && date && date > endDate) return false;
			if (
				locationFilter &&
				String(s.location_id || "") !== String(locationFilter)
			)
				return false;
			if (
				employeeFilter &&
				String(s.square_id || "") !== String(employeeFilter)
			)
				return false;
			if (isShiftLeadScoped) {
				if (!date) return false;
				const locs = shiftLeadDays[date];
				const loc = s.location_id;
				if (!Array.isArray(locs) || locs.length === 0) return false;
				if (loc == null || loc === "" || !locs.includes(String(loc)))
					return false;
			}
			return true;
		});
	}, [
		schedules,
		startDate,
		endDate,
		locationFilter,
		employeeFilter,
		isShiftLeadScoped,
		shiftLeadDays,
	]);

	const leadDayOverrideKeys = useMemo(() => {
		const keys = new Set();
		(leadDayAssignments || []).forEach((row) => {
			const employeeId = Number(row.employee_id);
			const date = String(row.assignment_date || "").slice(0, 10);
			if (!Number.isFinite(employeeId) || !date) return;
			keys.add(`${employeeId}|${date}`);
			const locationId = String(row.location_id || "").trim();
			if (locationId) keys.add(`${employeeId}|${date}|${locationId}`);
		});
		return keys;
	}, [leadDayAssignments]);

	const teamMemberLocalIdBySquareId = useMemo(() => {
		const map = new Map();
		(teamMembers || []).forEach((tm) => {
			if (tm?.id == null || tm.local_id == null) return;
			const localId = Number(tm.local_id);
			if (!Number.isFinite(localId)) return;
			map.set(String(tm.id), localId);
		});
		// Day-assignment rows carry square_id so ★ mapping works without loading
		// the create/edit roster endpoint for view-only users.
		(leadDayAssignments || []).forEach((row) => {
			if (row?.square_id == null || row?.employee_id == null) return;
			const localId = Number(row.employee_id);
			if (!Number.isFinite(localId)) return;
			map.set(String(row.square_id), localId);
		});
		return map;
	}, [teamMembers, leadDayAssignments]);

	const localIdsHaveLeadDayOverride = useCallback(
		(localIds, date, locationId = null) => {
			if (!canLoadShiftLeadDayOverrides || !date || !localIds?.size) {
				return false;
			}
			const loc = locationId != null ? String(locationId).trim() : "";
			for (const employeeId of localIds) {
				if (loc) {
					if (
						leadDayOverrideKeys.has(`${employeeId}|${date}|${loc}`)
					) {
						return true;
					}
					continue;
				}
				if (leadDayOverrideKeys.has(`${employeeId}|${date}`)) {
					return true;
				}
			}
			return false;
		},
		[canLoadShiftLeadDayOverrides, leadDayOverrideKeys],
	);

	const memberHasLeadDayOverride = useCallback(
		(member, date, locationId = null) => {
			if (!canLoadShiftLeadDayOverrides || !date) return false;
			const localIds = new Set();
			(member?.schedules || []).forEach((schedule) => {
				const id = Number(schedule.employee_id);
				if (Number.isFinite(id)) localIds.add(id);
			});
			const squareId = String(member?.key || "").startsWith("id:")
				? String(member.key).slice(3)
				: null;
			if (squareId && teamMemberLocalIdBySquareId.has(squareId)) {
				localIds.add(teamMemberLocalIdBySquareId.get(squareId));
			}
			return localIdsHaveLeadDayOverride(localIds, date, locationId);
		},
		[
			canLoadShiftLeadDayOverrides,
			localIdsHaveLeadDayOverride,
			teamMemberLocalIdBySquareId,
		],
	);

	const timecardHasLeadDayOverride = useCallback(
		(timecard) => {
			if (!timecard) return false;
			const date = timecardDateString(timecard);
			const localIds = new Set();
			const localEmployeeId =
				timecard.localEmployeeId ?? timecard.local_employee_id;
			if (localEmployeeId != null) {
				const n = Number(localEmployeeId);
				if (Number.isFinite(n)) localIds.add(n);
			}
			const squareId = timecard.teamMemberId || timecard.team_member_id;
			if (
				squareId != null &&
				teamMemberLocalIdBySquareId.has(String(squareId))
			) {
				localIds.add(teamMemberLocalIdBySquareId.get(String(squareId)));
			}
			return localIdsHaveLeadDayOverride(
				localIds,
				date,
				timecard.locationId || timecard.location_id || null,
			);
		},
		[localIdsHaveLeadDayOverride, teamMemberLocalIdBySquareId],
	);

	const restrictedDatesInRange = useMemo(() => {
		if (!isShiftLeadScoped) return [];
		const dates = new Set();
		restrictedDates.forEach((date) => {
			if (startDate && date < startDate) return;
			if (endDate && date > endDate) return;
			dates.add(date);
		});
		// When previewing (or any case where the API returned an unscoped roster),
		// derive greyed-out days from loaded punches vs the previewed lead-day map.
		timecards.forEach((tc) => {
			const date = timecardDateString(tc);
			if (!date) return;
			if (startDate && date < startDate) return;
			if (endDate && date > endDate) return;
			const locs = shiftLeadDays[date];
			if (!Array.isArray(locs) || locs.length === 0) dates.add(date);
		});
		return Array.from(dates).sort();
	}, [
		isShiftLeadScoped,
		restrictedDates,
		timecards,
		shiftLeadDays,
		startDate,
		endDate,
	]);

	const restrictedDateSet = useMemo(
		() => new Set(restrictedDatesInRange),
		[restrictedDatesInRange],
	);

	// Only employees with a loaded timecard are selectable, so the list never offers empty results
	const employeeOptions = useMemo(() => {
		const byId = new Map();
		timecards.forEach((tc) => {
			if (!tc.teamMemberId) return;
			if (
				locationFilter &&
				String(tc.locationId || "") !== String(locationFilter)
			)
				return;
			if (!byId.has(tc.teamMemberId)) {
				byId.set(tc.teamMemberId, tc.teamMemberName || tc.teamMemberId);
			}
		});
		return Array.from(byId.entries())
			.map(([id, name]) => ({ id, name }))
			.sort((a, b) =>
				String(a.name).localeCompare(String(b.name), undefined, {
					sensitivity: "base",
				}),
			);
	}, [timecards, locationFilter]);

	useEffect(() => {
		if (
			employeeFilter &&
			!employeeOptions.some((option) => option.id === employeeFilter)
		) {
			setEmployeeFilter("");
		}
	}, [employeeFilter, employeeOptions]);

	const sortedTimecards = useMemo(() => {
		const sorted = [...filteredTimecards];
		sorted.sort((a, b) => {
			const aVal = getSortValue(a, sortBy);
			const bVal = getSortValue(b, sortBy);
			let comparison = 0;
			if (typeof aVal === "number" && typeof bVal === "number") {
				comparison = aVal - bVal;
			} else {
				comparison = String(aVal).localeCompare(
					String(bVal),
					undefined,
					{ sensitivity: "base" },
				);
			}
			return sortOrder === "asc" ? comparison : -comparison;
		});
		return sorted;
	}, [filteredTimecards, sortBy, sortOrder, locations]);

	// Per-day totals for the calendar column headers (keeps the toolbar from shifting)
	const dailyLaborSummaries = useMemo(() => {
		const byDate = {};

		filteredTimecards.forEach((tc) => {
			const date = timecardDateString(tc);
			if (!date) return;

			if (!byDate[date]) {
				byDate[date] = {
					totalMinutes: 0,
					hoursKnown: false,
					totalCostCents: 0,
					costKnown: false,
					currency: "USD",
				};
			}

			const bucket = byDate[date];
			const worked = getWorkedMinutes(tc);
			if (worked != null) {
				bucket.totalMinutes += worked;
				bucket.hoursKnown = true;
			}
			if (!canViewWages) return;
			const cost = getLaborCostCents(tc);
			if (cost != null) {
				bucket.totalCostCents += cost;
				bucket.costKnown = true;
				if (tc.wage?.hourlyRate?.currency)
					bucket.currency = tc.wage.hourlyRate.currency;
			}
		});

		return Object.fromEntries(
			Object.entries(byDate).map(([date, bucket]) => [
				date,
				{
					totalHours: bucket.hoursKnown
						? bucket.totalMinutes / 60
						: null,
					totalCostCents: bucket.costKnown
						? bucket.totalCostCents
						: null,
					currency: bucket.currency,
				},
			]),
		);
	}, [filteredTimecards, canViewWages]);

	const renderSortableHeader = (column, label, width, colClass = "") => (
		<th
			className={`sortable-header ${colClass}${sortBy === column ? " sort-active" : ""}`.trim()}
			onClick={() => handleSort(column)}
			style={{
				width,
				cursor: "pointer",
				backgroundColor: sortBy === column ? "#ebebeb" : undefined,
			}}
			title={`Click to sort by ${label.toLowerCase()}`}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.35rem",
				}}
			>
				<span>{label}</span>
				{sortBy === column && (
					<span
						style={{
							fontSize: "0.875rem",
							fontWeight: "bold",
							color: "#333333",
						}}
					>
						{sortOrder === "asc" ? "↑" : "↓"}
					</span>
				)}
			</div>
		</th>
	);

	const formatBreakDuration = (breakItem) => {
		if (!breakItem.startAt || !breakItem.endAt) {
			return breakItem.expectedDuration || "N/A";
		}
		try {
			const start = parseISO(breakItem.startAt);
			const end = parseISO(breakItem.endAt);
			const minutes = differenceInMinutes(end, start);
			return `${minutes}m`;
		} catch (e) {
			return breakItem.expectedDuration || "N/A";
		}
	};

	const formatMoney = (amount, currency = "USD") => {
		if (amount === null || amount === undefined) return "-";
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: currency,
			minimumFractionDigits: 2,
		}).format(amount / 100); // Square amounts are in cents
	};

	const formatJobOptionLabel = (job) => {
		const jobTitle = job.title || "Untitled job";
		if (!canViewWages || !job.wage) {
			return jobTitle;
		}

		const parsedWage = Number.parseFloat(job.wage);
		if (Number.isNaN(parsedWage)) {
			return `${jobTitle} - ${job.wage}`;
		}

		return `${jobTitle} - $${parsedWage.toFixed(2)}/hr`;
	};

	// Resolve start/end bounds for a timecard (open shifts end at "now")
	const getTimecardBounds = useCallback(
		(timecard) => {
			if (!timecard.startAt) return null;
			const start = parseISO(timecard.startAt);
			const date = toAppDateString(start);
			const isOpen = timecard.status === "OPEN" && !timecard.endAt;
			let end = null;
			if (isOpen) {
				end = new Date(nowMs);
			} else if (timecard.endAt) {
				end = parseISO(timecard.endAt);
			}
			if (!end) return null;
			return {
				start,
				end,
				date,
				isOpen,
				durationMinutes: differenceInMinutes(end, start),
			};
		},
		[nowMs],
	);

	// Punch times as minutes from Seattle midnight of the punch's own day, so they
	// can be compared against schedule times (which are plain "HH:mm" clock values).
	const getTimecardMinutes = (timecard) => {
		const bounds = getTimecardBounds(timecard);
		if (!bounds) return null;
		const dayStart = seattleWallToUtcDate(bounds.date, "00:00:00");
		return {
			start: differenceInMinutes(bounds.start, dayStart),
			end: differenceInMinutes(bounds.end, dayStart),
			isOpen: bounds.isOpen,
		};
	};

	const scheduleMemberName = (schedule) =>
		[schedule.first_name, schedule.last_name].filter(Boolean).join(" ") ||
		schedule.square_id ||
		"Unknown";

	// O(1) lookups instead of filtering the full lists per cell/row.
	const schedulesByMemberDate = useMemo(() => {
		const map = new Map();
		schedules.forEach((schedule) => {
			const key = memberDateKey(schedule.square_id, schedule.shift_date);
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(schedule);
		});
		return map;
	}, [schedules]);

	const timecardsByMemberDate = useMemo(() => {
		const map = new Map();
		timecards.forEach((timecard) => {
			const date = timecardDateString(timecard);
			if (!date || !timecard.teamMemberId) return;
			const key = memberDateKey(timecard.teamMemberId, date);
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(timecard);
		});
		return map;
	}, [timecards]);

	const chainsByMemberDate = useMemo(() => {
		const map = new Map();
		schedulesByMemberDate.forEach((daySchedules, key) => {
			map.set(key, buildContiguousScheduleChains(daySchedules));
		});
		return map;
	}, [schedulesByMemberDate]);

	// One calendar row per person, merging punches and scheduled shifts for that person
	const calendarMembers = useMemo(() => {
		const members = new Map();

		const ensureMember = (key, name) => {
			if (!members.has(key)) {
				members.set(key, { key, name, timecards: [], schedules: [] });
			}
			const member = members.get(key);
			if (name && (member.name === "Unknown" || !member.name)) {
				member.name = name;
			}
			return member;
		};

		filteredTimecards.forEach((timecard) => {
			const name =
				timecard.teamMemberName || timecard.teamMemberId || "Unknown";
			const key = timecard.teamMemberId
				? `id:${timecard.teamMemberId}`
				: `name:${name}`;
			ensureMember(key, name).timecards.push(timecard);
		});

		filteredSchedules.forEach((schedule) => {
			const name = scheduleMemberName(schedule);
			const key = schedule.square_id
				? `id:${schedule.square_id}`
				: `name:${name}`;
			ensureMember(key, name).schedules.push(schedule);
		});

		return Array.from(members.values()).sort((a, b) =>
			String(a.name).localeCompare(String(b.name), undefined, {
				sensitivity: "base",
			}),
		);
	}, [filteredTimecards, filteredSchedules]);

	// Columns cover punches, schedules, and shift-lead-restricted days (greyed out)
	const dateRange = useMemo(() => {
		const dates = new Set();
		filteredTimecards.forEach((tc) => {
			if (!tc.startAt) return;
			dates.add(toAppDateString(parseISO(tc.startAt)));
		});
		filteredSchedules.forEach((s) => {
			if (!s.shift_date) return;
			dates.add(String(s.shift_date));
		});
		restrictedDatesInRange.forEach((date) => dates.add(date));
		return Array.from(dates).sort();
	}, [filteredTimecards, filteredSchedules, restrictedDatesInRange]);

	const hasRawData =
		timecards.length > 0 ||
		schedules.length > 0 ||
		restrictedDates.length > 0;
	const hasFilteredData =
		filteredTimecards.length > 0 ||
		filteredSchedules.length > 0 ||
		restrictedDatesInRange.length > 0;
	const hasCalendarData = dateRange.length > 0;
	const filtersActive = !!locationFilter || !!employeeFilter;

	// Per-day scale: zoom to the day's longest clock-in (not a full 24h day).
	// Origin is the earliest clock-in; length is at least the longest duration so bars stay readable.
	// Scheduled shifts share the scale so dashed bars line up with the punches they belong to.
	const dayScales = useMemo(
		() =>
			dateRange.reduce((acc, date) => {
				// One "now" for the whole day so open shifts compare consistently against schedules
				const now = new Date(nowMs);
				const endMinute = (value) =>
					Math.floor(value.getTime() / 60000);
				let earliestStart = null;
				let latestEnd = null;
				let latestOpenEnd = null;
				let maxDurationMinutes = 0;

				filteredTimecards.forEach((tc) => {
					const bounds = getTimecardBounds(tc);
					if (!bounds || bounds.date !== date) return;
					const end = bounds.isOpen ? now : bounds.end;
					const durationMinutes = Math.max(
						differenceInMinutes(end, bounds.start),
						0,
					);
					if (!earliestStart || bounds.start < earliestStart)
						earliestStart = bounds.start;
					if (!latestEnd || end > latestEnd) latestEnd = end;
					if (
						bounds.isOpen &&
						(!latestOpenEnd || end > latestOpenEnd)
					)
						latestOpenEnd = end;
					if (durationMinutes > maxDurationMinutes) {
						maxDurationMinutes = durationMinutes;
					}
				});

				filteredSchedules.forEach((schedule) => {
					if (String(schedule.shift_date) !== date) return;
					const scheduleBounds = getScheduleBounds(schedule);
					if (!scheduleBounds) return;
					const dayOrigin = seattleWallToUtcDate(date, "00:00:00");
					const start = new Date(
						dayOrigin.getTime() + scheduleBounds.start * 60000,
					);
					const end = new Date(
						dayOrigin.getTime() + scheduleBounds.end * 60000,
					);
					if (!earliestStart || start < earliestStart)
						earliestStart = start;
					if (!latestEnd || end > latestEnd) latestEnd = end;
					const durationMinutes =
						scheduleBounds.end - scheduleBounds.start;
					if (durationMinutes > maxDurationMinutes) {
						maxDurationMinutes = durationMinutes;
					}
				});

				if (!earliestStart || !latestEnd) {
					acc[date] = {
						scaleStart: null,
						scaleMinutes: 60,
						hourDivisions: 1,
					};
					return acc;
				}

				// If an open "Now" shift reaches the latest minute, count Now as the latest end
				// (schedules/closed punches are exact :00, so Now can otherwise lose by a few seconds).
				const latestIsNow =
					!!latestOpenEnd &&
					endMinute(latestOpenEnd) >= endMinute(latestEnd);
				if (latestIsNow && latestOpenEnd > latestEnd) {
					latestEnd = latestOpenEnd;
				}

				const spanMinutes = Math.max(
					differenceInMinutes(latestEnd, earliestStart),
					1,
				);
				// Prefer longest clock-in as the scale; extend if staggered shifts need more room
				const baseScaleMinutes = Math.max(
					maxDurationMinutes,
					spanMinutes,
					1,
				);
				// When an open ("now") shift is the right edge, leave room so the bar isn't flush.
				const rightPadMinutes = latestIsNow
					? Math.max(30, Math.round(baseScaleMinutes * 0.1))
					: 0;
				const scaleMinutes = baseScaleMinutes + rightPadMinutes;
				const hourDivisions = Math.max(1, Math.ceil(scaleMinutes / 60));

				acc[date] = {
					scaleStart: earliestStart,
					scaleMinutes,
					hourDivisions,
				};
				return acc;
			}, {}),
		[
			dateRange,
			filteredTimecards,
			filteredSchedules,
			getTimecardBounds,
			nowMs,
		],
	);

	// Calculate time position as percentage of the day's scaled window
	const getTimePosition = (timecard, scale) => {
		const bounds = getTimecardBounds(timecard);
		if (!bounds) return { left: 0, width: 0, date: "", isOpen: false };

		const scaleMinutes = scale?.scaleMinutes || 60;
		const scaleStart = scale?.scaleStart || bounds.start;
		const offsetMinutes = differenceInMinutes(bounds.start, scaleStart);
		const leftPercent = (offsetMinutes / scaleMinutes) * 100;
		const widthPercent = (bounds.durationMinutes / scaleMinutes) * 100;

		return {
			left: leftPercent,
			width: widthPercent,
			date: bounds.date,
			isOpen: bounds.isOpen,
		};
	};

	const getSchedulePosition = (schedule, date, scale) => {
		const scheduleBounds = getScheduleBounds(schedule);
		if (!scheduleBounds || !scale?.scaleStart) return null;

		const dayOrigin = seattleWallToUtcDate(date, "00:00:00");
		const start = new Date(
			dayOrigin.getTime() + scheduleBounds.start * 60000,
		);
		const scaleMinutes = scale.scaleMinutes || 60;
		const offsetMinutes = differenceInMinutes(start, scale.scaleStart);
		const durationMinutes = scheduleBounds.end - scheduleBounds.start;

		return {
			left: (offsetMinutes / scaleMinutes) * 100,
			width: (durationMinutes / scaleMinutes) * 100,
		};
	};

	// Every schedule for this person on this punch's day, from the unfiltered set so a
	// location filter can't hide the schedule that a punch should be matched against.
	const schedulesForTimecard = useCallback(
		(timecard) => {
			if (
				!canViewSchedules ||
				!timecard?.startAt ||
				!timecard.teamMemberId
			) {
				return [];
			}
			const date = timecardDateString(timecard);
			if (!date) return [];
			return (
				schedulesByMemberDate.get(
					memberDateKey(timecard.teamMemberId, date),
				) || []
			);
		},
		[canViewSchedules, schedulesByMemberDate],
	);

	const getMatchedScheduleChain = (timecard, chains) => {
		const punch = getTimecardMinutes(timecard);
		if (!punch || !chains || chains.length === 0) return null;

		const overlapping = chains.filter(
			(chain) => chain.start < punch.end && chain.end > punch.start,
		);
		if (overlapping.length === 0) return null;

		return overlapping.reduce((best, chain) =>
			Math.abs(chain.start - punch.start) <
			Math.abs(best.start - punch.start)
				? chain
				: best,
		);
	};

	const matchedChainFor = (timecard, daySchedules) => {
		if (!canViewSchedules || !schedulesReady) return null;
		const date = timecardDateString(timecard);
		const key = memberDateKey(timecard.teamMemberId, date);
		const chains =
			chainsByMemberDate.get(key) ||
			buildContiguousScheduleChains(daySchedules);
		return getMatchedScheduleChain(timecard, chains);
	};

	const isTimecardUnscheduled = (timecard, daySchedules) => {
		if (!canViewSchedules || !schedulesReady) return false;
		const date = timecardDateString(timecard);
		// Don't paint red for days we haven't loaded schedules for (e.g. after a date tweak)
		if (!date || !scheduleDatesLoaded.has(date)) return false;
		return !matchedChainFor(timecard, daySchedules);
	};

	const getClockInVariance = (timecard, daySchedules) => {
		const chain = matchedChainFor(timecard, daySchedules);
		if (!chain) return null;
		const punch = getTimecardMinutes(timecard);
		if (!punch) return null;

		const delta = punch.start - chain.start;
		if (Math.abs(delta) <= CLOCK_VARIANCE_MINUTES) return null;

		const direction = delta < 0 ? "early" : "late";
		return {
			minutes: delta,
			label: `Clocked in ${formatVarianceMinutes(delta)} ${direction} (scheduled ${formatScheduleClock(chain.start)})`,
		};
	};

	const getClockOutVariance = (timecard, daySchedules) => {
		const chain = matchedChainFor(timecard, daySchedules);
		if (!chain) return null;
		const punch = getTimecardMinutes(timecard);
		// An in-progress shift hasn't clocked out yet, so "early" would always be true
		if (!punch || punch.isOpen) return null;

		const delta = punch.end - chain.end;
		const isLate = delta > CLOCK_VARIANCE_MINUTES;
		// Short early-outs are normal; only long ones are worth surfacing
		const isEarly =
			delta < 0 && Math.abs(delta) >= CLOCK_OUT_EARLY_FLAG_MINUTES;
		if (!isLate && !isEarly) return null;

		const direction = isLate ? "late" : "early";
		return {
			minutes: delta,
			label: `Clocked out ${formatVarianceMinutes(delta)} ${direction} (scheduled ${formatScheduleClock(chain.end)})`,
		};
	};

	const getWrongLocation = (timecard, daySchedules) => {
		const chain = matchedChainFor(timecard, daySchedules);
		if (!chain || chain.locationIds.length === 0) return null;
		if (chain.locationIds.includes(String(timecard.locationId || "")))
			return null;

		const scheduledNames = chain.locationIds.map(
			(id) => locations.find((loc) => String(loc.id) === id)?.name || id,
		);
		return {
			locationIds: chain.locationIds,
			label: `Scheduled at ${scheduledNames.join(", ")}`,
		};
	};

	// Scheduled shift with no matching punch once start + grace has passed.
	// Returns how much of the shift to shade: start → now while the shift is still
	// running, or the whole shift once it's over.
	const getScheduleMissedClockIn = (schedule, date) => {
		if (!canViewSchedules || !schedulesReady) return null;
		if (!date || !scheduleDatesLoadedRef.current.has(date)) return null;
		if (!schedule?.square_id) return null;

		const bounds = getScheduleBounds(schedule);
		if (!bounds) return null;

		const dayOrigin = seattleWallToUtcDate(date, "00:00:00");
		const shiftStart = new Date(dayOrigin.getTime() + bounds.start * 60000);
		const shiftEnd = new Date(dayOrigin.getTime() + bounds.end * 60000);
		const graceEnd = new Date(
			shiftStart.getTime() + CLOCK_VARIANCE_MINUTES * 60000,
		);
		const now = new Date(nowMs);
		if (now < graceEnd) return null;

		const key = memberDateKey(schedule.square_id, date);
		const daySchedules = schedulesByMemberDate.get(key) || [];
		const dayTimecards = timecardsByMemberDate.get(key) || [];
		const chains =
			chainsByMemberDate.get(key) ||
			buildContiguousScheduleChains(daySchedules);
		const clockedIn = dayTimecards.some((timecard) => {
			const chain = getMatchedScheduleChain(timecard, chains);
			return chain?.schedules?.some(
				(s) =>
					(s.id != null &&
						schedule.id != null &&
						s.id === schedule.id) ||
					s === schedule,
			);
		});
		if (clockedIn) return null;

		const shiftMinutes = bounds.end - bounds.start;
		const elapsedMinutes = differenceInMinutes(now, shiftStart);
		const inProgress = now < shiftEnd;
		const fillPercent = inProgress
			? Math.min(100, Math.max(0, (elapsedMinutes / shiftMinutes) * 100))
			: 100;

		return { fillPercent, elapsedMinutes, inProgress };
	};

	// One pass per timecard so the table, calendar, and modal all agree on the flags
	const getTimecardFlags = (timecard) => {
		const daySchedules = schedulesForTimecard(timecard);
		return {
			unscheduled: isTimecardUnscheduled(timecard, daySchedules),
			clockIn: getClockInVariance(timecard, daySchedules),
			clockOut: getClockOutVariance(timecard, daySchedules),
			wrongLocation: getWrongLocation(timecard, daySchedules),
		};
	};

	const flagsByTimecardId = useMemo(() => {
		const map = new Map();
		filteredTimecards.forEach((timecard) => {
			if (timecard?.id == null) return;
			map.set(timecard.id, getTimecardFlags(timecard));
		});
		return map;
	}, [
		filteredTimecards,
		canViewSchedules,
		schedulesReady,
		schedulesByMemberDate,
		chainsByMemberDate,
		locations,
		nowMs,
	]);

	const calendarView = useMemo(() => {
		if (!hasCalendarData) return [];
		const nowIso = new Date(nowMs).toISOString();
		const membersForView =
			calendarMembers.length > 0
				? calendarMembers
				: restrictedDateSet.size > 0
					? [
							{
								key: "shift-lead-restricted",
								name: "",
								timecards: [],
								schedules: [],
							},
						]
					: [];
		return membersForView.map((member) => {
			let memberMinutes = 0;
			let memberHoursKnown = false;
			let memberCostCents = 0;
			let memberCostKnown = false;
			let memberCurrency = "USD";

			member.timecards.forEach((tc) => {
				const worked = getWorkedMinutes(tc);
				if (worked != null) {
					memberMinutes += worked;
					memberHoursKnown = true;
				}
				if (!canViewWages) return;
				const cost = getLaborCostCents(tc);
				if (cost != null) {
					memberCostCents += cost;
					memberCostKnown = true;
					if (tc.wage?.hourlyRate?.currency) {
						memberCurrency = tc.wage.hourlyRate.currency;
					}
				}
			});

			const memberHoursLabel = memberHoursKnown
				? formatHoursTotal(memberMinutes / 60)
				: null;
			const memberLaborLabel =
				canViewWages && memberCostKnown
					? formatMoney(memberCostCents, memberCurrency)
					: null;
			const statsLabel = [memberHoursLabel, memberLaborLabel]
				.filter(Boolean)
				.join(" · ");

			const cells = dateRange.map((date) => {
				const restricted = restrictedDateSet.has(date);
				const leadOverride = memberHasLeadDayOverride(
					member,
					date,
					locationFilter || null,
				);
				if (restricted) {
					return {
						date,
						hourDivisions: 1,
						scheduledBars: [],
						punchBars: [],
						restricted: true,
						leadOverride,
					};
				}
				const dateTimecards = member.timecards.filter((tc) => {
					if (!tc.startAt) return false;
					return timecardDateString(tc) === date;
				});
				const dateSchedules = member.schedules.filter(
					(s) => String(s.shift_date) === date,
				);
				const dayScale = dayScales[date];
				const scheduledBars = dateSchedules
					.map((schedule, idx) => {
						const position = getSchedulePosition(
							schedule,
							date,
							dayScale,
						);
						if (!position) return null;
						const missedClockIn = getScheduleMissedClockIn(
							schedule,
							date,
						);
						const scheduleTitle = [
							`Scheduled ${formatScheduleClock(schedule.start_time)} – ${formatScheduleClock(schedule.end_time)}`,
							canViewRoles ? schedule.role_name || null : null,
							missedClockIn
								? `No clock-in (${formatVarianceMinutes(
										missedClockIn.elapsedMinutes,
									)} past start)`
								: null,
						].filter(Boolean);
						return {
							key: schedule.id || `schedule-${idx}`,
							position,
							missedClockIn,
							title: scheduleTitle.join("\n"),
						};
					})
					.filter(Boolean);

				const punchBars = dateTimecards
					.map((timecard, idx) => {
						const position = getTimePosition(timecard, dayScale);
						if (position.date !== date) return null;
						const bounds = getTimecardBounds(timecard);
						const breakOverlays = getPunchBreakOverlays(
							timecard,
							bounds,
							nowMs,
						);
						const flags =
							flagsByTimecardId.get(timecard.id) ||
							getTimecardFlags(timecard);
						const jobTitle = (timecard.wage?.title || "").trim();
						const hourlyRate = timecard.wage?.hourlyRate?.amount
							? formatMoney(
									timecard.wage.hourlyRate.amount,
									timecard.wage.hourlyRate.currency,
								)
							: "-";
						const endTimeDisplay = position.isOpen
							? "Now"
							: timecard.endAt
								? formatAppTime(timecard.endAt)
								: "-";
						const durationLabel = position.isOpen
							? formatDuration(timecard.startAt, nowIso)
							: formatDuration(timecard.startAt, timecard.endAt);
						const laborCostCents = canViewWages
							? getLaborCostCents(timecard)
							: null;
						const shiftCost =
							laborCostCents != null
								? formatMoney(
										laborCostCents,
										timecard.wage?.hourlyRate?.currency,
									)
								: null;
						const flagKind = flags.unscheduled
							? "unscheduled"
							: flags.clockIn || flags.clockOut
								? "variance"
								: flags.wrongLocation
									? "wrong-location"
									: null;
						const barClasses = [
							"gantt-bar",
							position.isOpen ? "gantt-bar-open" : null,
							flagKind && !position.isOpen
								? `gantt-bar-${flagKind}`
								: null,
							flagKind && position.isOpen
								? `gantt-bar-flag gantt-bar-flag-${flagKind}`
								: null,
						]
							.filter(Boolean)
							.join(" ");
						const titleParts = [
							`${formatAppTime(timecard.startAt)} – ${endTimeDisplay}`,
							durationLabel,
							canViewRoles ? jobTitle || null : null,
							canViewWages ? hourlyRate : null,
							timecard.status || "CLOSED",
							flags.unscheduled ? "Unscheduled" : null,
							flags.clockIn ? flags.clockIn.label : null,
							flags.clockOut ? flags.clockOut.label : null,
							flags.wrongLocation
								? flags.wrongLocation.label
								: null,
							...breakOverlays.map((overlay) => overlay.label),
						].filter(Boolean);
						return {
							key: timecard.id || idx,
							timecard,
							position,
							barClasses,
							breakOverlays,
							title: titleParts.join("\n"),
							startLabel: formatAppTime(timecard.startAt),
							endLabel: endTimeDisplay,
							durationLabel,
							jobLabel:
								canViewRoles && jobTitle ? jobTitle : null,
							shiftCost,
						};
					})
					.filter(Boolean);

				return {
					date,
					hourDivisions: dayScale?.hourDivisions || 1,
					scheduledBars,
					punchBars,
					restricted: false,
					leadOverride,
				};
			});

			return {
				key: member.key,
				name: member.name,
				statsLabel,
				cells,
			};
		});
	}, [
		hasCalendarData,
		calendarMembers,
		dateRange,
		dayScales,
		flagsByTimecardId,
		canViewWages,
		canViewRoles,
		nowMs,
		schedulesByMemberDate,
		timecardsByMemberDate,
		chainsByMemberDate,
		canViewSchedules,
		schedulesReady,
		restrictedDateSet,
		memberHasLeadDayOverride,
		locationFilter,
	]);

	const missedScheduleRows = useMemo(() => {
		if (!canViewSchedules || !schedulesReady) return [];
		const rows = [];
		filteredSchedules.forEach((schedule) => {
			const date = String(schedule.shift_date || "");
			const missed = getScheduleMissedClockIn(schedule, date);
			if (!missed) return;
			const bounds = getScheduleBounds(schedule);
			if (!bounds) return;

			const dayOrigin = seattleWallToUtcDate(date, "00:00:00");
			const startAt = new Date(
				dayOrigin.getTime() + bounds.start * 60000,
			).toISOString();
			const endAt = new Date(
				dayOrigin.getTime() + bounds.end * 60000,
			).toISOString();
			const locationName =
				locations.find(
					(loc) =>
						String(loc.id) === String(schedule.location_id || ""),
				)?.name || null;

			rows.push({
				_kind: "missed-schedule",
				id: `missed-schedule-${schedule.id || `${schedule.square_id}-${date}-${bounds.start}`}`,
				startAt,
				endAt,
				status: "MISSED",
				teamMemberId: schedule.square_id || null,
				teamMemberName: scheduleMemberName(schedule),
				locationId: schedule.location_id || null,
				locationName,
				wage: schedule.role_name ? { title: schedule.role_name } : null,
				breaks: [],
				schedule,
				missed,
			});
		});
		return rows;
	}, [
		canViewSchedules,
		schedulesReady,
		filteredSchedules,
		schedulesByMemberDate,
		timecardsByMemberDate,
		chainsByMemberDate,
		locations,
		nowMs,
	]);

	const tableRows = useMemo(() => {
		const combined = [...sortedTimecards, ...missedScheduleRows];
		combined.sort((a, b) => {
			const aVal = getSortValue(a, sortBy);
			const bVal = getSortValue(b, sortBy);
			let comparison = 0;
			if (typeof aVal === "number" && typeof bVal === "number") {
				comparison = aVal - bVal;
			} else {
				comparison = String(aVal).localeCompare(
					String(bVal),
					undefined,
					{ sensitivity: "base" },
				);
			}
			return sortOrder === "asc" ? comparison : -comparison;
		});
		return combined;
	}, [sortedTimecards, missedScheduleRows, sortBy, sortOrder, locations]);

	const mobileDayGroups = useMemo(() => {
		const groups = [];
		const indexByDay = new Map();
		for (const row of tableRows) {
			const day =
				timecardDateString(row) ||
				(row.startAt
					? String(toAppDateString(parseISO(row.startAt))).slice(
							0,
							10,
						)
					: "unknown");
			let group = indexByDay.get(day);
			if (!group) {
				group = {
					day,
					label: day === "unknown" ? "Unknown date" : day,
					rows: [],
				};
				indexByDay.set(day, group);
				groups.push(group);
			}
			group.rows.push(row);
		}
		for (const group of groups) {
			if (group.day === "unknown") continue;
			group.label = formatWorkdayLabel(group.day, "EEE, MMM d") || group.day;
		}
		return groups;
	}, [tableRows]);

	// Keep the phone calendar day in range after fetch / filter changes.
	useEffect(() => {
		if (!dateRange.length) {
			setMobileCalendarDay(null);
			return;
		}
		setMobileCalendarDay((prev) => {
			if (prev && dateRange.includes(prev)) return prev;
			const today = toAppDateString(new Date());
			if (dateRange.includes(today)) return today;
			return dateRange[0];
		});
	}, [dateRange]);

	const mobileCalendarMembers = useMemo(() => {
		if (!mobileCalendarDay) return [];
		return calendarView
			.map((member) => {
				const cell = member.cells.find(
					(c) => c.date === mobileCalendarDay,
				);
				if (!cell) return null;
				if (
					!cell.restricted &&
					!cell.leadOverride &&
					cell.scheduledBars.length === 0 &&
					cell.punchBars.length === 0
				) {
					return null;
				}
				return {
					key: member.key,
					name: member.name,
					cell,
				};
			})
			.filter(Boolean);
	}, [calendarView, mobileCalendarDay]);

	const mobileHourTicks = useMemo(() => {
		const scale = mobileCalendarDay ? dayScales[mobileCalendarDay] : null;
		if (!scale?.scaleStart) return [];
		const mins = scale.scaleMinutes || 60;
		const divisions = Math.max(1, scale.hourDivisions || 1);
		const step = divisions <= 4 ? 1 : Math.ceil(divisions / 4);
		const ticks = [];
		for (let i = 0; i <= divisions; i += step) {
			const offset = Math.min(i * 60, mins);
			const at = new Date(scale.scaleStart.getTime() + offset * 60000);
			ticks.push({
				key: String(i),
				left: (offset / mins) * 100,
				label: formatAppTime(at.toISOString()),
			});
		}
		if (ticks.length && ticks[ticks.length - 1].left < 99.5) {
			const at = new Date(scale.scaleStart.getTime() + mins * 60000);
			ticks.push({
				key: "end",
				left: 100,
				label: formatAppTime(at.toISOString()),
			});
		}
		return ticks;
	}, [mobileCalendarDay, dayScales]);

	const selectedTimecardFlags = selectedTimecard
		? flagsByTimecardId.get(selectedTimecard.id) ||
			getTimecardFlags(selectedTimecard)
		: null;
	const selectedLaborCostCents =
		selectedTimecard && canViewWages
			? getLaborCostCents(selectedTimecard)
			: null;

	const mobileFilterSummary = useMemo(() => {
		const fmt = (value) => {
			if (!value) return "";
			return formatWorkdayLabel(value, "MMM d") || value;
		};
		const range = `${fmt(startDate)} – ${fmt(endDate)}`;
		const bits = [range];
		if (locationFilter) {
			const loc = locations.find(
				(l) => String(l.id) === String(locationFilter),
			);
			bits.push(loc?.name || "Location");
		}
		if (employeeFilter) {
			const emp = employeeOptions.find(
				(o) => String(o.id) === String(employeeFilter),
			);
			bits.push(emp?.name || "Employee");
		}
		return bits.join(" · ");
	}, [
		startDate,
		endDate,
		locationFilter,
		employeeFilter,
		locations,
		employeeOptions,
	]);

	const renderGanttBreakOverlays = (overlays) =>
		(overlays || []).map((overlay) => {
			const width = Math.max(overlay.width, 0.4);
			const right = Math.max(0, 100 - overlay.left - width);
			return (
				<div
					key={overlay.key}
					className={`gantt-bar-break ${
						overlay.isPaid
							? "gantt-bar-break-paid"
							: "gantt-bar-break-unpaid"
					}`}
					aria-hidden="true"
					style={{
						clipPath: `inset(0 ${right}% 0 ${overlay.left}%)`,
					}}
				/>
			);
		});

	if (!canViewTimecards) {
		return <NoPermissions />;
	}

	return (
		<div
			className={`time-cards${isNarrow ? " is-narrow" : ""}${isNarrow && mobileFiltersOpen ? " filters-open" : ""}`}
		>
			<div className="page-header time-cards-header">
				{!isNarrow && <h2>Timecards</h2>}

				{(hasPermission("view_timecards") ||
					hasPermission("view_timecards_as_shift_lead") ||
					hasPermission("superuser")) && (
					<div className="time-cards-toolbar">
						{isNarrow && (
							<div className="time-cards-mobile-chrome">
								<button
									type="button"
									className={`time-cards-filter-toggle${mobileFiltersOpen ? " is-open" : ""}${filtersActive ? " has-filters" : ""}`}
									aria-expanded={mobileFiltersOpen}
									onClick={() =>
										setMobileFiltersOpen((open) => !open)
									}
								>
									<span className="time-cards-filter-toggle-label">
										{mobileFiltersOpen
											? "Hide filters"
											: "Filters"}
									</span>
									<span className="time-cards-filter-toggle-summary">
										{mobileFilterSummary}
									</span>
								</button>
								<label className="time-cards-mobile-view">
									<span className="time-cards-mobile-view-label">
										View
									</span>
									<select
										aria-label="Timecard view"
										value={
											viewMode === "calendar"
												? "calendar"
												: "list"
										}
										onChange={(e) =>
											setViewMode(e.target.value)
										}
									>
										<option value="list">List</option>
										<option value="calendar">
											Calendar
										</option>
									</select>
								</label>
								<button
									type="button"
									className="btn btn-secondary btn-header-action time-cards-mobile-fetch"
									onClick={handleFetch}
									disabled={loading || syncing}
								>
									{loading || syncing ? "…" : "Fetch"}
								</button>
								{hasPermission("create_timecards") && (
									<button
										type="button"
										className="btn btn-primary time-cards-create-btn"
										onClick={() => setShowCreateModal(true)}
										aria-label="Create timecard"
										title="Create timecard"
									>
										<span aria-hidden="true">+</span>
									</button>
								)}
							</div>
						)}
						<div
							className={`time-cards-toolbar-group${isNarrow ? " time-cards-mobile-filters" : ""}`}
							hidden={isNarrow && !mobileFiltersOpen}
						>
							<div className="filter-group">
								<label htmlFor="start-date">Start</label>
								<input
									id="start-date"
									type="date"
									value={startDate}
									min={
										shiftLeadAllowedWeek?.startDate ||
										undefined
									}
									max={
										shiftLeadAllowedWeek?.endDate ||
										undefined
									}
									onChange={(e) => {
										let next = e.target.value;
										if (
											isShiftLeadScoped &&
											shiftLeadAllowedWeek?.startDate &&
											next <
												shiftLeadAllowedWeek.startDate
										) {
											next =
												shiftLeadAllowedWeek.startDate;
										}
										if (
											isShiftLeadScoped &&
											shiftLeadAllowedWeek?.endDate &&
											next > shiftLeadAllowedWeek.endDate
										) {
											next = shiftLeadAllowedWeek.endDate;
										}
										setStartDate(next);
									}}
								/>
							</div>
							<div className="filter-group">
								<label htmlFor="end-date">End</label>
								<input
									id="end-date"
									type="date"
									value={endDate}
									min={
										shiftLeadAllowedWeek?.startDate ||
										undefined
									}
									max={
										shiftLeadAllowedWeek?.endDate ||
										undefined
									}
									onChange={(e) => {
										let next = e.target.value;
										if (
											isShiftLeadScoped &&
											shiftLeadAllowedWeek?.startDate &&
											next <
												shiftLeadAllowedWeek.startDate
										) {
											next =
												shiftLeadAllowedWeek.startDate;
										}
										if (
											isShiftLeadScoped &&
											shiftLeadAllowedWeek?.endDate &&
											next > shiftLeadAllowedWeek.endDate
										) {
											next = shiftLeadAllowedWeek.endDate;
										}
										setEndDate(next);
									}}
								/>
							</div>
							<div className="filter-group">
								<label htmlFor="location-filter">
									Location
								</label>
								<select
									id="location-filter"
									value={locationFilter}
									onChange={(e) =>
										setLocationFilter(e.target.value)
									}
								>
									<option value="">All locations</option>
									{locations.map((loc) => (
										<option key={loc.id} value={loc.id}>
											{loc.name}
										</option>
									))}
								</select>
							</div>
							<div className="filter-group">
								<label htmlFor="employee-filter">
									Employee
								</label>
								<select
									id="employee-filter"
									value={employeeFilter}
									onChange={(e) =>
										setEmployeeFilter(e.target.value)
									}
								>
									<option value="">All employees</option>
									{employeeOptions.map((option) => (
										<option
											key={option.id}
											value={option.id}
										>
											{option.name}
										</option>
									))}
								</select>
							</div>
							{!isNarrow && (
								<button
									type="button"
									className="btn btn-secondary btn-header-action"
									onClick={handleFetch}
									disabled={loading || syncing}
								>
									{loading || syncing ? "Loading…" : "Fetch"}
								</button>
							)}
						</div>

						{!isNarrow && (
							<>
								<div
									className="time-cards-toolbar-divider"
									aria-hidden="true"
								/>
								<div
									className="time-cards-view-toggle"
									role="group"
									aria-label="Timecard view"
								>
									<button
										type="button"
										className={`btn btn-secondary${viewMode === "table" ? " is-active" : ""}`}
										aria-pressed={viewMode === "table"}
										onClick={() => setViewMode("table")}
									>
										Table
									</button>
									<button
										type="button"
										className={`btn btn-secondary${viewMode === "calendar" ? " is-active" : ""}`}
										aria-pressed={viewMode === "calendar"}
										onClick={() => setViewMode("calendar")}
									>
										Calendar
									</button>
								</div>
							</>
						)}

						{!isNarrow && hasPermission("create_timecards") && (
							<button
								type="button"
								className="btn btn-primary time-cards-create-btn"
								onClick={() => setShowCreateModal(true)}
								aria-label="Create timecard"
								title="Create timecard"
							>
								<span aria-hidden="true">+</span>
							</button>
						)}
					</div>
				)}
			</div>

			{(hasPermission("view_timecards") ||
				hasPermission("view_timecards_as_shift_lead") ||
				hasPermission("superuser")) && (
				<div className="time-cards-body">
					{error && (
						<div
							className="error-message"
							style={{ color: "#d32f2f" }}
						>
							{error}
						</div>
					)}

					{(loading || syncing) && !hasRawData && (
						<div className="loading">Loading timecards...</div>
					)}

					{!loading && !hasRawData && !error && !syncing && (
						<div className="empty-state">
							<div className="empty-state-content">
								<p className="empty-state-title">
									No timecards found
								</p>
								<p className="empty-state-message">
									Select a date range and click Fetch to load
									timecards from Square.
								</p>
							</div>
						</div>
					)}

					{!loading &&
						hasRawData &&
						!hasFilteredData &&
						filtersActive &&
						!error && (
							<div className="empty-state">
								<div className="empty-state-content">
									<p className="empty-state-title">
										No matches for these filters
									</p>
									<p className="empty-state-message">
										Clear the location or employee filter to
										see the rest of the range.
									</p>
								</div>
							</div>
						)}

					{!loading &&
						hasRawData &&
						!hasFilteredData &&
						!filtersActive &&
						!error &&
						!syncing && (
							<div className="empty-state">
								<div className="empty-state-content">
									<p className="empty-state-title">
										No data in this date range
									</p>
									<p className="empty-state-message">
										Click Fetch to load timecards for these
										dates. Previously fetched ranges stay
										cached.
									</p>
								</div>
							</div>
						)}

					{!loading &&
						hasFilteredData &&
						(viewMode === "table" || viewMode === "list") &&
						tableRows.length === 0 &&
						!error && (
							<div className="empty-state">
								<div className="empty-state-content">
									<p className="empty-state-title">
										{restrictedDatesInRange.length > 0
											? "No viewable timecards in this range"
											: "No timecards in this range"}
									</p>
									<p className="empty-state-message">
										{restrictedDatesInRange.length > 0
											? ""
											: canViewSchedules
												? "Scheduled shifts exist here, but nobody is past the clock-in window yet. Switch to the calendar to see them."
												: "No punches found for these filters."}
									</p>
								</div>
							</div>
						)}

					{tableRows.length > 0 && viewMode === "list" && (
						<div className="time-cards-panel timecard-mobile-list">
							{mobileDayGroups.map((group) => (
								<section
									key={group.day}
									className="timecard-mobile-day"
								>
									<h3 className="timecard-mobile-day-label">
										{group.label}
										<span className="timecard-mobile-day-count">
											{group.rows.length}
										</span>
									</h3>
									<ul className="timecard-mobile-cards">
										{group.rows.map((row) => {
											const isMissed =
												row._kind === "missed-schedule";
											const flags = isMissed
												? null
												: flagsByTimecardId.get(
														row.id,
													) || getTimecardFlags(row);
											const startLabel = row.startAt
												? formatAppTime(row.startAt)
												: "—";
											const endLabel =
												!isMissed &&
												row.status === "OPEN" &&
												!row.endAt
													? "Now"
													: row.endAt
														? formatAppTime(
																row.endAt,
															)
														: "—";
											const durationLabel =
												!isMissed &&
												row.status === "OPEN" &&
												!row.endAt
													? formatDuration(
															row.startAt,
															new Date(
																nowMs,
															).toISOString(),
														)
													: formatDuration(
															row.startAt,
															row.endAt,
														);
											const locationLabel =
												row.locationName ||
												locations.find(
													(l) =>
														String(l.id) ===
														String(
															row.locationId ||
																"",
														),
												)?.name ||
												row.locationId ||
												"—";
											const jobLabel = canViewRoles
												? row.wage?.title || null
												: null;
											const laborCostCents =
												!isMissed && canViewWages
													? getLaborCostCents(row)
													: null;
											const costLabel =
												laborCostCents != null
													? formatMoney(
															laborCostCents,
															row.wage?.hourlyRate
																?.currency ||
																"USD",
														)
													: null;
											const canEditThis =
												!isMissed &&
												canEditTimecardScoped(row) &&
												row.status === "CLOSED" &&
												!isOwnTimecard(row);
											const canDeleteThis =
												!isMissed &&
												hasPermission(
													"delete_timecards",
												) &&
												!isOwnTimecard(row);
											const statusLabel = isMissed
												? "Missed"
												: row.status || "—";
											const statusClass = isMissed
												? "status-missed"
												: row.status === "CLOSED"
													? "status-closed"
													: row.status === "OPEN"
														? "status-open"
														: "status-other";
											const flagNotes = [];
											if (isMissed && row.missed) {
												flagNotes.push(
													`No clock-in (${formatVarianceMinutes(row.missed.elapsedMinutes)} past start)`,
												);
											}
											if (flags?.unscheduled)
												flagNotes.push("Unscheduled");
											if (flags?.clockIn)
												flagNotes.push(
													flags.clockIn.label,
												);
											if (flags?.clockOut)
												flagNotes.push(
													flags.clockOut.label,
												);
											if (flags?.wrongLocation)
												flagNotes.push(
													flags.wrongLocation.label,
												);
											// Match calendar gantt bar colors / priority.
											const accentKind = isMissed
												? "missed"
												: flags?.unscheduled
													? "unscheduled"
													: flags?.clockIn ||
														  flags?.clockOut
														? "variance"
														: flags?.wrongLocation
															? "wrong-location"
															: row.status ===
																  "OPEN"
																? "open"
																: "ok";

											return (
												<li
													key={row.id}
													className={`timecard-mobile-card is-${accentKind}`}
												>
													<button
														type="button"
														className="timecard-mobile-card-main"
														onClick={() => {
															if (isMissed)
																return;
															setSelectedTimecard(
																row,
															);
															setShowInfoModal(
																true,
															);
														}}
														disabled={isMissed}
													>
														<div className="timecard-mobile-card-top">
															<span className="timecard-mobile-card-name">
																{row.teamMemberName ||
																	row.teamMemberId ||
																	"Unknown"}
																{!isMissed &&
																timecardHasLeadDayOverride(
																	row,
																) ? (
																	<span className="timecard-lead-override">
																		★
																	</span>
																) : null}
															</span>
															<span
																className={`timecard-mobile-status ${statusClass}`}
															>
																{statusLabel}
															</span>
														</div>
														<div className="timecard-mobile-card-time">
															{startLabel} –{" "}
															{endLabel}
															<span>
																{" "}
																·{" "}
																{durationLabel}
															</span>
															{costLabel ? (
																<span className="timecard-mobile-card-cost">
																	{" "}
																	·{" "}
																	{costLabel}
																</span>
															) : null}
														</div>
														{(jobLabel ||
															(locationLabel &&
																locationLabel !==
																	"—")) && (
															<div className="timecard-mobile-card-meta">
																{[
																	jobLabel,
																	locationLabel !==
																	"—"
																		? locationLabel
																		: null,
																]
																	.filter(
																		Boolean,
																	)
																	.join(
																		" · ",
																	)}
															</div>
														)}
														{flagNotes.length >
															0 && (
															<div className="timecard-mobile-card-flags">
																{flagNotes[0]}
															</div>
														)}
													</button>
													{canEditThis && (
														<button
															type="button"
															className="timecard-mobile-edit"
															onClick={() =>
																handleEditClick(
																	row,
																)
															}
														>
															Edit
														</button>
													)}
													{!canEditThis &&
														canDeleteThis && (
															<button
																type="button"
																className="timecard-mobile-edit is-danger"
																onClick={() =>
																	handleDeleteClick(
																		row,
																	)
																}
															>
																Delete
															</button>
														)}
												</li>
											);
										})}
									</ul>
								</section>
							))}
						</div>
					)}

					{tableRows.length > 0 && viewMode === "table" && (
						<div className="time-cards-panel timecard-results">
							<table className="spreadsheet-table timecard-results-table">
								<thead>
									<tr>
										{renderSortableHeader(
											"date",
											"Date",
											"12%",
											"col-date",
										)}
										{renderSortableHeader(
											"startAt",
											"Start Time",
											"12%",
											"col-start",
										)}
										{renderSortableHeader(
											"endAt",
											"End Time",
											"12%",
											"col-end",
										)}
										{renderSortableHeader(
											"duration",
											"Duration",
											"10%",
											"col-duration",
										)}
										{renderSortableHeader(
											"teamMember",
											"Team Member",
											"13%",
											"col-member",
										)}
										{renderSortableHeader(
											"location",
											"Location",
											"12%",
											"col-location",
										)}
										{canViewRoles &&
											renderSortableHeader(
												"jobTitle",
												"Job Title",
												"9%",
												"col-job",
											)}
										{canViewWages &&
											renderSortableHeader(
												"hourlyRate",
												"Hourly Rate",
												"8%",
												"col-rate",
											)}
										{canViewWages &&
											renderSortableHeader(
												"laborCost",
												"Labor Cost",
												"8%",
												"col-cost",
											)}
										{canViewEstimatedTips &&
											renderSortableHeader(
												"estimatedTips",
												"Est. Tips",
												"8%",
												"col-tips",
											)}
										{renderSortableHeader(
											"status",
											"Status",
											"8%",
											"col-status",
										)}
										{renderSortableHeader(
											"breaks",
											"Breaks",
											"12%",
											"col-breaks",
										)}
										{(hasPermission("delete_timecards") ||
											canEditTimecards) && (
											<th
												className="col-actions"
												style={{ width: "8%" }}
											>
												Actions
											</th>
										)}
									</tr>
								</thead>
								<tbody>
									{tableRows.map((row) => {
										if (row._kind === "missed-schedule") {
											const startLabel = formatAppTime(
												row.startAt,
											);
											const endLabel = formatAppTime(
												row.endAt,
											);
											const durationLabel =
												formatDuration(
													row.startAt,
													row.endAt,
												);
											const locationLabel =
												row.locationName ||
												row.locationId ||
												"—";
											const missedTitle = `No clock-in (${formatVarianceMinutes(
												row.missed.elapsedMinutes,
											)} past start)`;
											return (
												<tr
													key={row.id}
													className="timecard-row-missed"
													title={missedTitle}
												>
													<td
														className="col-date"
														data-label="Date"
													>
														<span className="timecard-date-label">
															{formatAppDate(
																row.startAt,
															)}
														</span>
														<span className="timecard-mobile-shift">
															{startLabel}
															{" – "}
															{endLabel}
															<span className="timecard-mobile-duration">
																{" · "}
																{durationLabel}
															</span>
														</span>
													</td>
													<td
														className="col-start"
														data-label="Start"
													>
														{startLabel}
													</td>
													<td
														className="col-end"
														data-label="End"
													>
														{endLabel}
													</td>
													<td
														className="col-duration"
														data-label="Duration"
													>
														{durationLabel}
														<TimecardAlertMark
															className="timecard-unscheduled-alert"
															message={
																missedTitle
															}
														/>
													</td>
													<td
														className="col-member"
														data-label="Team Member"
													>
														{row.teamMemberName ? (
															<strong>
																{
																	row.teamMemberName
																}
															</strong>
														) : (
															<code
																style={{
																	fontSize:
																		"0.75rem",
																}}
															>
																{row.teamMemberId ||
																	"N/A"}
															</code>
														)}
													</td>
													<td
														className="col-location"
														data-label="Location"
													>
														{locationLabel}
													</td>
													{canViewRoles && (
														<td
															className={`col-job${!row.wage?.title ? " is-empty" : ""}`}
															data-label="Job"
														>
															{row.wage?.title ||
																"—"}
														</td>
													)}
													{canViewWages && (
														<td
															className="col-rate is-empty"
															data-label="Rate"
														>
															—
														</td>
													)}
													{canViewWages && (
														<td
															className="col-cost is-empty"
															data-label="Cost"
														>
															—
														</td>
													)}
													{canViewEstimatedTips && (
														<td
															className="col-tips is-empty"
															data-label="Est. Tips"
														>
															—
														</td>
													)}
													<td
														className="col-status"
														data-label="Status"
													>
														<span
															className="status-badge status-missed"
															title={missedTitle}
														>
															Missed
														</span>
													</td>
													<td
														className="col-breaks is-empty"
														data-label="Breaks"
													>
														—
													</td>
													{(hasPermission(
														"delete_timecards",
													) ||
														canEditTimecards) && (
														<td
															className="col-actions is-empty"
															data-label="Actions"
														>
															—
														</td>
													)}
												</tr>
											);
										}

										const timecard = row;
										const flags =
											flagsByTimecardId.get(
												timecard.id,
											) || getTimecardFlags(timecard);
										const laborCostCents = canViewWages
											? getLaborCostCents(timecard)
											: null;
										const startLabel = timecard.startAt
											? formatAppTime(timecard.startAt)
											: "N/A";
										const endLabel =
											timecard.status === "OPEN" &&
											!timecard.endAt
												? "Now"
												: timecard.endAt
													? formatAppTime(
															timecard.endAt,
														)
													: "N/A";
										const durationLabel =
											timecard.status === "OPEN" &&
											!timecard.endAt
												? formatDuration(
														timecard.startAt,
														new Date(
															nowMs,
														).toISOString(),
													)
												: formatDuration(
														timecard.startAt,
														timecard.endAt,
													);
										const locationLabel =
											timecard.locationName ||
											locations.find(
												(l) =>
													l.id ===
													timecard.locationId,
											)?.name ||
											timecard.locationId ||
											"—";
										return (
											<tr
												key={timecard.id}
												className="timecard-row-clickable"
												onClick={() => {
													setSelectedTimecard(
														timecard,
													);
													setShowInfoModal(true);
												}}
											>
												<td
													className="col-date"
													data-label="Date"
												>
													<span className="timecard-date-label">
														{timecard.startAt
															? formatAppDate(
																	timecard.startAt,
																)
															: "N/A"}
														{timecardHasLeadDayOverride(
															timecard,
														) ? (
															<span
																className="timecard-lead-override"
																title="Marked as shift lead this day (manual override)"
																aria-label="Marked as shift lead this day"
															>
																★
															</span>
														) : null}
													</span>
													<span className="timecard-mobile-shift">
														{startLabel}
														{flags.clockIn && (
															<TimecardAlertMark
																className="timecard-variance-alert"
																message={
																	flags
																		.clockIn
																		.label
																}
															/>
														)}
														{" – "}
														{endLabel}
														{flags.clockOut && (
															<TimecardAlertMark
																className="timecard-variance-alert"
																message={
																	flags
																		.clockOut
																		.label
																}
															/>
														)}
														<span className="timecard-mobile-duration">
															{" · "}
															{durationLabel}
															{flags.unscheduled && (
																<TimecardAlertMark
																	className="timecard-unscheduled-alert"
																	message="Unscheduled"
																/>
															)}
														</span>
													</span>
												</td>
												<td
													className="col-start"
													data-label="Start"
												>
													{startLabel}
													{flags.clockIn && (
														<TimecardAlertMark
															className="timecard-variance-alert"
															message={
																flags.clockIn
																	.label
															}
														/>
													)}
												</td>
												<td
													className="col-end"
													data-label="End"
												>
													{endLabel}
													{flags.clockOut && (
														<TimecardAlertMark
															className="timecard-variance-alert"
															message={
																flags.clockOut
																	.label
															}
														/>
													)}
												</td>
												<td
													className="col-duration"
													data-label="Duration"
												>
													{durationLabel}
													{flags.unscheduled && (
														<TimecardAlertMark
															className="timecard-unscheduled-alert"
															message="Unscheduled"
														/>
													)}
												</td>
												<td
													className="col-member"
													data-label="Team Member"
												>
													{timecard.teamMemberName ? (
														<strong>
															{
																timecard.teamMemberName
															}
														</strong>
													) : (
														<code
															style={{
																fontSize:
																	"0.75rem",
															}}
														>
															{timecard.teamMemberId ||
																"N/A"}
														</code>
													)}
												</td>
												<td
													className="col-location"
													data-label="Location"
												>
													{locationLabel}
													{flags.wrongLocation && (
														<TimecardAlertMark
															className="timecard-location-alert"
															message={
																flags
																	.wrongLocation
																	.label
															}
														/>
													)}
												</td>
												{canViewRoles && (
													<td
														className={`col-job${!timecard.wage?.title ? " is-empty" : ""}`}
														data-label="Job"
													>
														{timecard.wage?.title ||
															"—"}
													</td>
												)}
												{canViewWages && (
													<td
														className={`col-rate${!timecard.wage?.hourlyRate ? " is-empty" : ""}`}
														data-label="Rate"
													>
														{timecard.wage
															?.hourlyRate
															? formatMoney(
																	timecard
																		.wage
																		.hourlyRate
																		.amount,
																	timecard
																		.wage
																		.hourlyRate
																		.currency,
																)
															: "—"}
													</td>
												)}
												{canViewWages && (
													<td
														className={`col-cost${laborCostCents == null ? " is-empty" : ""}`}
														data-label="Cost"
													>
														{laborCostCents != null
															? formatMoney(
																	laborCostCents,
																	timecard
																		.wage
																		?.hourlyRate
																		?.currency ||
																		"USD",
																)
															: "—"}
													</td>
												)}
												{canViewEstimatedTips && (
													<td
														className={`col-tips${
															timecard
																.estimatedTips
																?.amount == null
																? " is-empty"
																: ""
														}`}
														data-label="Est. Tips"
														title={
															timecard.wage
																?.tipEligible ===
															false
																? "Not tip eligible"
																: undefined
														}
													>
														{timecard.estimatedTips
															?.amount != null
															? formatMoney(
																	timecard
																		.estimatedTips
																		.amount,
																	timecard
																		.estimatedTips
																		.currency ||
																		"USD",
																)
															: timecard.wage
																		?.tipEligible ===
																  false
																? "N/A"
																: "—"}
													</td>
												)}
												<td
													className="col-status"
													data-label="Status"
												>
													<span
														className={`status-badge ${
															timecard.status ===
															"CLOSED"
																? "status-closed"
																: timecard.status ===
																	  "OPEN"
																	? "status-open"
																	: "status-other"
														}`}
													>
														{timecard.status || "—"}
													</span>
												</td>
												<td
													className={`col-breaks${!(timecard.breaks && timecard.breaks.length > 0) ? " is-empty" : ""}`}
													data-label="Breaks"
												>
													{timecard.breaks &&
													timecard.breaks.length >
														0 ? (
														<div className="timecard-breaks-cell">
															{timecard.breaks.map(
																(
																	breakItem,
																	idx,
																) => (
																	<div
																		key={
																			breakItem.id ||
																			idx
																		}
																	>
																		{breakItem.name ||
																			"Break"}
																		:{" "}
																		{formatBreakDuration(
																			breakItem,
																		)}
																		<span className="timecard-break-paid">
																			(
																			{formatBreakPayStatus(
																				breakItem,
																			)}
																			)
																		</span>
																	</div>
																),
															)}
														</div>
													) : (
														"—"
													)}
												</td>
												{(hasPermission(
													"delete_timecards",
												) ||
													canEditTimecards) && (
													<td
														className="col-actions"
														data-label="Actions"
													>
														<div className="timecard-row-actions">
															{canEditTimecardScoped(
																timecard,
															) &&
																timecard.status ===
																	"CLOSED" &&
																!isOwnTimecard(
																	timecard,
																) && (
																	<button
																		type="button"
																		className="btn btn-primary btn-sm"
																		onClick={(
																			e,
																		) => {
																			e.stopPropagation();
																			handleEditClick(
																				timecard,
																			);
																		}}
																	>
																		Edit
																	</button>
																)}
															{hasPermission(
																"delete_timecards",
															) &&
																!isOwnTimecard(
																	timecard,
																) && (
																	<button
																		type="button"
																		className="btn btn-secondary btn-sm"
																		onClick={(
																			e,
																		) => {
																			e.stopPropagation();
																			handleDeleteClick(
																				timecard,
																			);
																		}}
																	>
																		Delete
																	</button>
																)}
														</div>
													</td>
												)}
											</tr>
										);
									})}
								</tbody>
							</table>
						</div>
					)}

					{hasCalendarData && viewMode === "calendar" && isNarrow && (
						<div className="time-cards-panel timecard-mobile-calendar">
							<div
								className="timecard-mobile-cal-days"
								role="tablist"
								aria-label="Calendar days"
							>
								{dateRange.map((date) => {
									const restricted =
										restrictedDateSet.has(date);
									const daySummary =
										dailyLaborSummaries[date];
									const selected = date === mobileCalendarDay;
									const weekday =
										formatWorkdayLabel(date, "EEE") ||
										date.slice(5);
									const dayNum =
										formatWorkdayLabel(date, "d") || "";
									const hoursLabel =
										!restricted &&
										daySummary?.totalHours != null
											? formatHoursTotal(
													daySummary.totalHours,
												)
											: null;
									return (
										<button
											key={date}
											type="button"
											role="tab"
											aria-selected={selected}
											className={`timecard-mobile-cal-day${selected ? " is-selected" : ""}${restricted ? " is-restricted" : ""}`}
											onClick={() =>
												setMobileCalendarDay(date)
											}
											title={
												restricted
													? "You were not a shift lead that day"
													: hoursLabel || undefined
											}
										>
											<span className="timecard-mobile-cal-weekday">
												{weekday}
											</span>
											<span className="timecard-mobile-cal-date">
												{dayNum}
											</span>
											{restricted ? (
												<span className="timecard-mobile-cal-day-meta">
													—
												</span>
											) : hoursLabel ? (
												<span className="timecard-mobile-cal-day-meta">
													{hoursLabel}
												</span>
											) : (
												<span className="timecard-mobile-cal-day-meta">
													&nbsp;
												</span>
											)}
										</button>
									);
								})}
							</div>

							{(() => {
								const day = mobileCalendarDay;
								if (!day) return null;
								const restricted = restrictedDateSet.has(day);
								const daySummary = dailyLaborSummaries[day];
								const dayLabel =
									formatWorkdayLabel(day, "EEEE, MMM d") ||
									day;
								const hoursLabel =
									!restricted &&
									daySummary?.totalHours != null
										? formatHoursTotal(
												daySummary.totalHours,
											)
										: null;
								const laborLabel =
									!restricted &&
									canViewWages &&
									daySummary?.totalCostCents != null
										? formatMoney(
												daySummary.totalCostCents,
												daySummary.currency,
											)
										: null;
								const summaryParts = [
									hoursLabel,
									laborLabel,
								].filter(Boolean);
								return (
									<div className="timecard-mobile-cal-day-panel">
										<div className="timecard-mobile-cal-day-heading">
											<h3>{dayLabel}</h3>
											{restricted ? (
												<p className="timecard-mobile-cal-day-note">
													Not shift lead
												</p>
											) : summaryParts.length > 0 ? (
												<p className="timecard-mobile-cal-day-note">
													{summaryParts.join(" · ")}
												</p>
											) : null}
										</div>

										{restricted ? (
											<div className="timecard-mobile-cal-empty">
												You were not a shift lead this
												day.
											</div>
										) : mobileCalendarMembers.length ===
										  0 ? (
											<div className="timecard-mobile-cal-empty">
												No shifts or punches this day.
											</div>
										) : (
											<>
												{mobileHourTicks.length > 0 && (
													<div
														className="timecard-mobile-cal-hours"
														aria-hidden="true"
													>
														{mobileHourTicks.map(
															(tick) => (
																<span
																	key={
																		tick.key
																	}
																	className="timecard-mobile-cal-hour"
																	style={{
																		left: `${tick.left}%`,
																	}}
																>
																	{tick.label}
																</span>
															),
														)}
													</div>
												)}
												<ul className="timecard-mobile-cal-rows">
													{mobileCalendarMembers.map(
														(member) => {
															const cell =
																member.cell;
															return (
																<li
																	key={
																		member.key
																	}
																	className="timecard-mobile-cal-row"
																>
																	<div className="timecard-mobile-cal-member">
																		<span className="timecard-mobile-cal-member-name">
																			{member.name ||
																				"Team member"}
																			{cell.leadOverride ? (
																				<span
																					className="timecard-lead-override"
																					aria-label="Marked as shift lead this day"
																				>
																					★
																				</span>
																			) : null}
																		</span>
																	</div>
																	<div
																		className="timecard-mobile-cal-track"
																		style={{
																			"--hour-divisions":
																				cell.hourDivisions,
																		}}
																	>
																		<div className="gantt-timeline">
																			{cell.scheduledBars.map(
																				(
																					bar,
																				) => (
																					<div
																						key={
																							bar.key
																						}
																						className={[
																							"gantt-bar-scheduled",
																							bar.missedClockIn
																								? "gantt-bar-scheduled-missed"
																								: null,
																						]
																							.filter(
																								Boolean,
																							)
																							.join(
																								" ",
																							)}
																						style={{
																							left: `${bar.position.left}%`,
																							width: `${Math.max(bar.position.width, 0.5)}%`,
																						}}
																						title={
																							bar.title
																						}
																					>
																						{bar.missedClockIn && (
																							<div
																								className="gantt-bar-scheduled-missed-fill"
																								style={{
																									width: `${bar.missedClockIn.fillPercent}%`,
																								}}
																							/>
																						)}
																					</div>
																				),
																			)}
																			{cell.punchBars.map(
																				(
																					bar,
																				) => (
																					<button
																						key={
																							bar.key
																						}
																						type="button"
																						className={
																							bar.barClasses
																						}
																						style={{
																							left: `${bar.position.left}%`,
																							width: `${Math.max(bar.position.width, 0.5)}%`,
																						}}
																						title={
																							bar.title
																						}
																						aria-label={
																							bar.title
																						}
																						onClick={() => {
																							setSelectedTimecard(
																								bar.timecard,
																							);
																							setShowInfoModal(
																								true,
																							);
																						}}
																					>
																						{renderGanttBreakOverlays(
																							bar.breakOverlays,
																						)}
																						<div className="gantt-bar-content">
																							<div className="gantt-bar-time">
																								{
																									bar.startLabel
																								}{" "}
																								–{" "}
																								{
																									bar.endLabel
																								}
																							</div>
																							{bar.jobLabel ? (
																								<div className="gantt-bar-job">
																									{
																										bar.jobLabel
																									}
																								</div>
																							) : null}
																							<div className="gantt-bar-duration">
																								{
																									bar.durationLabel
																								}
																								{canViewWages &&
																								bar.shiftCost
																									? ` · ${bar.shiftCost}`
																									: ""}
																							</div>
																						</div>
																					</button>
																				),
																			)}
																		</div>
																	</div>
																</li>
															);
														},
													)}
												</ul>
											</>
										)}
									</div>
								);
							})()}
						</div>
					)}

					{hasCalendarData &&
						viewMode === "calendar" &&
						!isNarrow && (
							<div className="time-cards-panel">
								<div className="gantt-view">
									<div className="gantt-header">
										<div className="gantt-sidebar-header">
											Team Member
										</div>
										<div className="gantt-dates-container">
											{dateRange.map((date) => {
												const restricted =
													restrictedDateSet.has(date);
												const daySummary =
													dailyLaborSummaries[date];
												const hoursLabel =
													!restricted &&
													daySummary?.totalHours !=
														null
														? formatHoursTotal(
																daySummary.totalHours,
															)
														: null;
												const laborLabel =
													!restricted &&
													canViewWages &&
													daySummary?.totalCostCents !=
														null
														? formatMoney(
																daySummary.totalCostCents,
																daySummary.currency,
															)
														: null;
												const summaryParts = [
													hoursLabel,
													laborLabel,
												].filter(Boolean);
												return (
													<div
														key={date}
														className={`gantt-date-column${restricted ? " is-restricted" : ""}`}
														title={
															restricted
																? "You were not a shift lead that day"
																: undefined
														}
													>
														<div className="gantt-date-header">
															{formatWorkdayLabel(
																date,
																"EEE",
															)}
														</div>
														<div className="gantt-date-header">
															{formatWorkdayLabel(
																date,
																"MMM d",
															)}
														</div>
														{restricted ? (
															<div className="gantt-date-restricted-note">
																Not shift lead
															</div>
														) : summaryParts.length >
														  0 ? (
															<div
																className="gantt-date-summary"
																aria-live="polite"
															>
																{summaryParts.join(
																	" | ",
																)}
															</div>
														) : null}
													</div>
												);
											})}
										</div>
									</div>
									<div className="gantt-body">
										{calendarView.map((member) => (
											<div
												key={member.key}
												className="gantt-row"
											>
												<div className="gantt-sidebar">
													<div className="gantt-member-name">
														{member.name}
													</div>
													{member.statsLabel ? (
														<div className="gantt-member-stats">
															{member.statsLabel}
														</div>
													) : null}
												</div>
												<div className="gantt-timeline-container">
													{member.cells.map(
														(cell) => (
															<div
																key={cell.date}
																className={`gantt-date-cell${cell.restricted ? " is-restricted" : ""}${cell.leadOverride ? " has-lead-override" : ""}`}
																style={{
																	"--hour-divisions":
																		cell.hourDivisions,
																}}
																title={
																	cell.restricted
																		? "You were not a shift lead that day"
																		: cell.leadOverride
																			? "Marked as shift lead this day (manual override)"
																			: undefined
																}
															>
																{cell.leadOverride ? (
																	<span
																		className="gantt-lead-override"
																		aria-label="Marked as shift lead this day"
																	>
																		★
																	</span>
																) : null}
																{cell.restricted ? (
																	<div
																		className="gantt-date-cell-restricted"
																		aria-hidden="true"
																	/>
																) : (
																	<div className="gantt-timeline">
																		{cell.scheduledBars.map(
																			(
																				bar,
																			) => (
																				<div
																					key={
																						bar.key
																					}
																					className={[
																						"gantt-bar-scheduled",
																						bar.missedClockIn
																							? "gantt-bar-scheduled-missed"
																							: null,
																					]
																						.filter(
																							Boolean,
																						)
																						.join(
																							" ",
																						)}
																					style={{
																						left: `${bar.position.left}%`,
																						width: `${Math.max(bar.position.width, 0.5)}%`,
																					}}
																					title={
																						bar.title
																					}
																				>
																					{bar.missedClockIn && (
																						<div
																							className="gantt-bar-scheduled-missed-fill"
																							style={{
																								width: `${bar.missedClockIn.fillPercent}%`,
																							}}
																						/>
																					)}
																				</div>
																			),
																		)}
																		{cell.punchBars.map(
																			(
																				bar,
																			) => (
																				<div
																					key={
																						bar.key
																					}
																					className={
																						bar.barClasses
																					}
																					style={{
																						left: `${bar.position.left}%`,
																						width: `${Math.max(bar.position.width, 0.5)}%`,
																					}}
																					title={
																						bar.title
																					}
																					onClick={(
																						e,
																					) => {
																						e.stopPropagation();
																						setSelectedTimecard(
																							bar.timecard,
																						);
																						setShowInfoModal(
																							true,
																						);
																					}}
																				>
																					{renderGanttBreakOverlays(
																						bar.breakOverlays,
																					)}
																					<div className="gantt-bar-content">
																						<div className="gantt-bar-time">
																							{
																								bar.startLabel
																							}{" "}
																							–{" "}
																							{
																								bar.endLabel
																							}
																						</div>
																						{bar.jobLabel ? (
																							<div className="gantt-bar-job">
																								{
																									bar.jobLabel
																								}
																							</div>
																						) : null}
																						<div className="gantt-bar-duration">
																							{
																								bar.durationLabel
																							}
																						</div>
																						{canViewWages &&
																							bar.shiftCost && (
																								<div className="gantt-bar-cost">
																									{
																										bar.shiftCost
																									}
																								</div>
																							)}
																					</div>
																				</div>
																			),
																		)}
																	</div>
																)}
															</div>
														),
													)}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
						)}
				</div>
			)}

			<CreateTimecardModal
				isOpen={showCreateModal}
				onClose={closeCreateModal}
				onCreated={() => {
					void refreshAfterCreate();
					void loadLeadDayAssignments();
				}}
				teamMembers={teamMembers}
				selectableTeamMembers={selectableTeamMembers}
				breakTypes={breakTypes}
				locations={locations}
				loadingFormData={loadingFormData}
				ownTeamMemberIds={ownTeamMemberIds}
				currentUserEmployeeId={currentUserEmployeeId}
				canViewWages={canViewWages}
				canViewShiftLeadDayMarks={canViewShiftLeadDayMarks}
				canEditShiftLeadDayMarks={canEditShiftLeadDayMarks}
				isMarkedShiftLeadDay={isMarkedShiftLeadDay}
			/>

			{/* Delete Confirmation Modal */}
			<ConfirmationModal
				isOpen={!!deleteConfirmModal}
				title="Delete Timecard"
				message={deleteConfirmModal?.message || ""}
				confirmText={deleting ? "Deleting..." : "Delete"}
				cancelText="Cancel"
				onConfirm={confirmDeleteTimecard}
				onClose={() => !deleting && setDeleteConfirmModal(null)}
				confirmDisabled={deleting}
				variant="danger"
			/>

			<ConfirmationModal
				isOpen={showReviewEmailModal}
				title={
					reviewEmailSentTo
						? "Email sent"
						: "Email timecard review"
				}
				message={
					reviewEmailSentTo
						? `Sent a review request to ${reviewEmailSentTo}.`
						: selectedTimecard?.teamMemberName
							? `Ask someone to review ${selectedTimecard.teamMemberName}'s timecard for accuracy.`
							: "Ask someone to review this timecard for accuracy."
				}
				confirmText={
					sendingReviewEmail ? "Sending..." : "Send email"
				}
				cancelText={reviewEmailSentTo ? "OK" : "Cancel"}
				hideConfirm={!!reviewEmailSentTo}
				onConfirm={handleSendReviewEmail}
				onClose={closeReviewEmailModal}
				confirmDisabled={sendingReviewEmail}
				variant="primary"
			>
				{!reviewEmailSentTo && (
					<label className="timecard-review-email-field">
						<span>Who should receive this email?</span>
						<input
							type="email"
							className="timecard-review-email-input"
							value={reviewEmailTo}
							onChange={(e) => setReviewEmailTo(e.target.value)}
							placeholder="name@example.com"
							autoComplete="email"
							disabled={sendingReviewEmail}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									handleSendReviewEmail();
								}
							}}
						/>
					</label>
				)}
			</ConfirmationModal>

			{/* Timecard Info Modal */}
			{showInfoModal &&
				selectedTimecard &&
				createPortal(
					<div
						className="modal-overlay"
						onClick={() => setShowInfoModal(false)}
					>
						<div
							className="modal-content timecard-modal timecard-modal-sheet"
							onClick={(e) => e.stopPropagation()}
							style={{ padding: 0 }}
						>
							<div
								className="modal-header"
								style={{
									borderBottom: "1px solid #d0d0d0",
									padding: "0.75rem 1rem",
									backgroundColor: "#f8f8f8",
								}}
							>
								<h3
									style={{
										margin: 0,
										fontSize: "1rem",
										fontWeight: "600",
									}}
								>
									Timecard Details
								</h3>
								<button
									className="modal-close"
									onClick={() => setShowInfoModal(false)}
									style={{
										fontSize: "1.5rem",
										lineHeight: "1",
										padding: 0,
										width: "24px",
										height: "24px",
									}}
								>
									×
								</button>
							</div>
							<div style={{ padding: 0 }}>
								<table
									style={{
										width: "100%",
										borderCollapse: "collapse",
										fontSize: "0.875rem",
									}}
								>
									<tbody>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
													width: "30%",
												}}
											>
												Date
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.startAt
													? formatAppDate(
															selectedTimecard.startAt,
														)
													: "N/A"}
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												Status
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												<span
													className={`status-badge ${
														selectedTimecard.status ===
														"CLOSED"
															? "status-closed"
															: selectedTimecard.status ===
																  "OPEN"
																? "status-open"
																: "status-other"
													}`}
													style={{
														fontSize: "0.75rem",
														padding:
															"0.25rem 0.5rem",
													}}
												>
													{selectedTimecard.status ||
														"CLOSED"}
												</span>
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												Start Time
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.startAt
													? formatAppTime(
															selectedTimecard.startAt,
														)
													: "N/A"}
												{selectedTimecardFlags?.clockIn && (
													<TimecardAlertMark
														className="timecard-variance-alert"
														message={
															selectedTimecardFlags
																.clockIn.label
														}
													/>
												)}
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												End Time
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.status ===
													"OPEN" &&
												!selectedTimecard.endAt
													? "Now (Open)"
													: selectedTimecard.endAt
														? formatAppTime(
																selectedTimecard.endAt,
															)
														: "N/A"}
												{selectedTimecardFlags?.clockOut && (
													<TimecardAlertMark
														className="timecard-variance-alert"
														message={
															selectedTimecardFlags
																.clockOut.label
														}
													/>
												)}
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												Duration
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.status ===
													"OPEN" &&
												!selectedTimecard.endAt
													? formatDuration(
															selectedTimecard.startAt,
															new Date().toISOString(),
														)
													: formatDuration(
															selectedTimecard.startAt,
															selectedTimecard.endAt,
														)}
												{selectedTimecardFlags?.unscheduled && (
													<TimecardAlertMark
														className="timecard-unscheduled-alert"
														message="Unscheduled"
													/>
												)}
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												Team Member
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.teamMemberName ||
													selectedTimecard.teamMemberId ||
													"N/A"}
											</td>
										</tr>
										<tr
											style={{
												borderBottom:
													"1px solid #e0e0e0",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
												}}
											>
												Location
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.locationName ||
													locations.find(
														(l) =>
															l.id ===
															selectedTimecard.locationId,
													)?.name ||
													selectedTimecard.locationId ||
													"N/A"}
												{selectedTimecardFlags?.wrongLocation && (
													<TimecardAlertMark
														className="timecard-location-alert"
														message={
															selectedTimecardFlags
																.wrongLocation
																.label
														}
													/>
												)}
											</td>
										</tr>
										{canViewRoles && (
											<tr
												style={{
													borderBottom:
														"1px solid #e0e0e0",
												}}
											>
												<td
													style={{
														padding: "0.75rem 1rem",
														borderRight:
															"1px solid #e0e0e0",
														backgroundColor:
															"#f8f8f8",
														fontWeight: "500",
													}}
												>
													Job Title
												</td>
												<td
													style={{
														padding: "0.75rem 1rem",
													}}
												>
													{selectedTimecard.wage
														?.title || "-"}
												</td>
											</tr>
										)}
										{canViewWages && (
											<tr
												style={{
													borderBottom:
														"1px solid #e0e0e0",
												}}
											>
												<td
													style={{
														padding: "0.75rem 1rem",
														borderRight:
															"1px solid #e0e0e0",
														backgroundColor:
															"#f8f8f8",
														fontWeight: "500",
													}}
												>
													Hourly Rate
												</td>
												<td
													style={{
														padding: "0.75rem 1rem",
													}}
												>
													{selectedTimecard.wage
														?.hourlyRate
														? formatMoney(
																selectedTimecard
																	.wage
																	.hourlyRate
																	.amount,
																selectedTimecard
																	.wage
																	.hourlyRate
																	.currency,
															)
														: "-"}
												</td>
											</tr>
										)}
										{canViewWages && (
											<tr
												style={{
													borderBottom:
														"1px solid #e0e0e0",
												}}
											>
												<td
													style={{
														padding: "0.75rem 1rem",
														borderRight:
															"1px solid #e0e0e0",
														backgroundColor:
															"#f8f8f8",
														fontWeight: "500",
													}}
												>
													Labor Cost
												</td>
												<td
													style={{
														padding: "0.75rem 1rem",
													}}
												>
													{selectedLaborCostCents !=
													null
														? formatMoney(
																selectedLaborCostCents,
																selectedTimecard
																	.wage
																	?.hourlyRate
																	?.currency ||
																	"USD",
															)
														: "-"}
												</td>
											</tr>
										)}
										{canViewEstimatedTips && (
											<tr
												style={{
													borderBottom:
														"1px solid #e0e0e0",
												}}
											>
												<td
													style={{
														padding: "0.75rem 1rem",
														borderRight:
															"1px solid #e0e0e0",
														backgroundColor:
															"#f8f8f8",
														fontWeight: "500",
													}}
												>
													Estimated Tips
												</td>
												<td
													style={{
														padding: "0.75rem 1rem",
													}}
												>
													{selectedTimecard
														.estimatedTips
														?.amount != null
														? formatMoney(
																selectedTimecard
																	.estimatedTips
																	.amount,
																selectedTimecard
																	.estimatedTips
																	.currency ||
																	"USD",
															)
														: selectedTimecard.wage
																	?.tipEligible ===
															  false
															? "Not tip eligible"
															: "—"}
												</td>
											</tr>
										)}
										{canViewEstimatedTips &&
											selectedTimecard.declaredCashTip
												?.amount > 0 && (
												<tr
													style={{
														borderBottom:
															"1px solid #e0e0e0",
													}}
												>
													<td
														style={{
															padding:
																"0.75rem 1rem",
															borderRight:
																"1px solid #e0e0e0",
															backgroundColor:
																"#f8f8f8",
															fontWeight: "500",
														}}
													>
														Declared Cash Tips
													</td>
													<td
														style={{
															padding:
																"0.75rem 1rem",
														}}
													>
														{formatMoney(
															selectedTimecard
																.declaredCashTip
																.amount,
															selectedTimecard
																.declaredCashTip
																.currency ||
																"USD",
														)}
													</td>
												</tr>
											)}
										<tr
											style={{
												borderBottom:
													selectedTimecard.breaks &&
													selectedTimecard.breaks
														.length > 0
														? "1px solid #e0e0e0"
														: "none",
											}}
										>
											<td
												style={{
													padding: "0.75rem 1rem",
													borderRight:
														"1px solid #e0e0e0",
													backgroundColor: "#f8f8f8",
													fontWeight: "500",
													verticalAlign: "top",
												}}
											>
												Breaks
											</td>
											<td
												style={{
													padding: "0.75rem 1rem",
												}}
											>
												{selectedTimecard.breaks &&
												selectedTimecard.breaks.length >
													0 ? (
													<table
														style={{
															width: "100%",
															borderCollapse:
																"collapse",
															fontSize:
																"0.8125rem",
														}}
													>
														<thead>
															<tr
																style={{
																	borderBottom:
																		"1px solid #e0e0e0",
																	backgroundColor:
																		"#fafafa",
																}}
															>
																<th
																	style={{
																		padding:
																			"0.5rem",
																		textAlign:
																			"left",
																		fontWeight:
																			"500",
																		borderRight:
																			"1px solid #e0e0e0",
																	}}
																>
																	Name
																</th>
																<th
																	style={{
																		padding:
																			"0.5rem",
																		textAlign:
																			"left",
																		fontWeight:
																			"500",
																		borderRight:
																			"1px solid #e0e0e0",
																	}}
																>
																	Time
																</th>
																<th
																	style={{
																		padding:
																			"0.5rem",
																		textAlign:
																			"left",
																		fontWeight:
																			"500",
																		borderRight:
																			"1px solid #e0e0e0",
																	}}
																>
																	Duration
																</th>
																<th
																	style={{
																		padding:
																			"0.5rem",
																		textAlign:
																			"left",
																		fontWeight:
																			"500",
																	}}
																>
																	Type
																</th>
															</tr>
														</thead>
														<tbody>
															{selectedTimecard.breaks.map(
																(
																	breakItem,
																	idx,
																) => (
																	<tr
																		key={
																			breakItem.id ||
																			idx
																		}
																		style={{
																			borderBottom:
																				idx <
																				selectedTimecard
																					.breaks
																					.length -
																					1
																					? "1px solid #e0e0e0"
																					: "none",
																		}}
																	>
																		<td
																			style={{
																				padding:
																					"0.5rem",
																				borderRight:
																					"1px solid #e0e0e0",
																			}}
																		>
																			{breakItem.name ||
																				"Break"}
																		</td>
																		<td
																			style={{
																				padding:
																					"0.5rem",
																				borderRight:
																					"1px solid #e0e0e0",
																			}}
																		>
																			{breakItem.startAt &&
																			breakItem.endAt
																				? `${formatAppTime(breakItem.startAt)} - ${formatAppTime(breakItem.endAt)}`
																				: "-"}
																		</td>
																		<td
																			style={{
																				padding:
																					"0.5rem",
																				borderRight:
																					"1px solid #e0e0e0",
																			}}
																		>
																			{formatBreakDuration(
																				breakItem,
																			)}
																		</td>
																		<td
																			style={{
																				padding:
																					"0.5rem",
																			}}
																		>
																			{formatBreakPayStatus(
																				breakItem,
																			)}
																		</td>
																	</tr>
																),
															)}
														</tbody>
													</table>
												) : (
													<span
														style={{
															color: "#999",
														}}
													>
														No breaks
													</span>
												)}
											</td>
										</tr>
									</tbody>
								</table>
							</div>
							<div
								className="modal-actions"
								style={{
									padding: "0.75rem 1rem",
									borderTop: "1px solid #d0d0d0",
									display: "flex",
									justifyContent: "space-between",
									gap: "0.5rem",
									flexWrap: "wrap",
									backgroundColor: "#f8f8f8",
								}}
							>
								<div
									style={{
										display: "flex",
										gap: "0.5rem",
										flexWrap: "wrap",
									}}
								>
									{canSendReviewEmail && (
										<button
											type="button"
											className="btn btn-secondary timecard-review-email-btn"
											onClick={() => {
												setReviewEmailTo("");
												setReviewEmailSentTo("");
												setShowReviewEmailModal(true);
											}}
											aria-label="Email timecard review"
											title="Email a review request"
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												width="18"
												height="18"
												viewBox="0 0 24 24"
												fill="none"
												stroke="currentColor"
												strokeWidth="2"
												strokeLinecap="round"
												strokeLinejoin="round"
												aria-hidden="true"
											>
												<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
												<polyline points="22,6 12,13 2,6" />
											</svg>
										</button>
									)}
									{canEditTimecardScoped(selectedTimecard) &&
										selectedTimecard.status === "CLOSED" &&
										!isOwnTimecard(selectedTimecard) && (
											<button
												type="button"
												className="btn btn-primary"
												onClick={() =>
													handleEditClick(
														selectedTimecard,
													)
												}
												style={{
													fontSize: "0.875rem",
													padding: "0.5rem 1rem",
												}}
											>
												Edit
											</button>
										)}
									{hasPermission("delete_timecards") &&
										!isOwnTimecard(selectedTimecard) && (
											<button
												type="button"
												className="btn btn-secondary"
												onClick={() =>
													handleDeleteClick(
														selectedTimecard,
													)
												}
												style={{
													fontSize: "0.875rem",
													padding: "0.5rem 1rem",
												}}
											>
												Delete
											</button>
										)}
								</div>
								<button
									type="button"
									className="btn btn-secondary"
									onClick={() => setShowInfoModal(false)}
									style={{
										fontSize: "0.875rem",
										padding: "0.5rem 1rem",
									}}
								>
									Close
								</button>
							</div>
						</div>
					</div>,
					document.body,
				)}

			{/* Edit Timecard Modal */}
			{showEditModal &&
				selectedTimecard &&
				createPortal(
					<div
						className="modal-overlay"
						onClick={() =>
							!editSubmitting && setShowEditModal(false)
						}
					>
						<div
							className="modal-content timecard-modal timecard-modal-sheet"
							onClick={(e) => e.stopPropagation()}
						>
							<div className="modal-header">
								<h3>Edit Timecard</h3>
								<button
									className="modal-close"
									onClick={() =>
										!editSubmitting &&
										setShowEditModal(false)
									}
									disabled={editSubmitting}
								>
									×
								</button>
							</div>
							<form
								onSubmit={handleUpdateTimecard}
								className="timecard-modal-form"
							>
								<div className="timecard-modal-body">
									<section className="timecard-form-section">
										<div className="timecard-form-grid two-column">
											<div className="timecard-field">
												<label htmlFor="edit-team-member">
													Team Member
												</label>
												<select
													id="edit-team-member"
													value={
														editFormData.teamMemberId
													}
													disabled
													aria-readonly="true"
												>
													<option value="">
														Select a team member
													</option>
													{teamMembers.map((tm) => (
														<option
															key={tm.id}
															value={tm.id}
														>
															{tm.name}
														</option>
													))}
													{editFormData.teamMemberId &&
														!teamMembers.some(
															(tm) =>
																tm.id ===
																editFormData.teamMemberId,
														) && (
															<option
																value={
																	editFormData.teamMemberId
																}
															>
																{selectedTimecard?.teamMemberName ||
																	editFormData.teamMemberId}
															</option>
														)}
												</select>
											</div>

											<div className="timecard-field">
												<label htmlFor="edit-location">
													Location{" "}
													<span
														style={{
															color: "#d32f2f",
														}}
													>
														*
													</span>
												</label>
												<select
													id="edit-location"
													value={
														editFormData.locationId
													}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															locationId:
																e.target.value,
														})
													}
													required
												>
													<option value="">
														Select a location
													</option>
													{locations.map((loc) => (
														<option
															key={loc.id}
															value={loc.id}
														>
															{loc.name}
														</option>
													))}
												</select>
											</div>

											<div className="timecard-field">
												<label htmlFor="edit-job">
													Job Title
												</label>
												<select
													id="edit-job"
													value={editFormData.jobId}
													onChange={(e) => {
														const nextJobId =
															e.target.value;
														const selectedJob =
															jobs.find(
																(job) =>
																	getJobOptionValue(
																		job,
																	) ===
																	nextJobId,
															);
														setEditFormData({
															...editFormData,
															jobId: nextJobId,
															tipEligible:
																selectedJob
																	? !!selectedJob.tipEligible
																	: true,
														});
													}}
													disabled={loadingJobs}
												>
													<option value="">
														{loadingJobs
															? "Loading jobs…"
															: "No job assignment"}
													</option>
													{jobs.map((job) => (
														<option
															key={getJobOptionValue(
																job,
															)}
															value={getJobOptionValue(
																job,
															)}
														>
															{formatJobOptionLabel(
																job,
															)}
														</option>
													))}
												</select>
											</div>
										</div>

										<div className="timecard-form-grid two-column">
											<div className="timecard-field">
												<label htmlFor="edit-date">
													Date{" "}
													<span
														style={{
															color: "#d32f2f",
														}}
													>
														*
													</span>
												</label>
												<input
													id="edit-date"
													type="date"
													value={editFormData.date}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															date: e.target
																.value,
														})
													}
													required
												/>
											</div>

											<label className="timecard-checkbox-row">
												<input
													type="checkbox"
													checked={
														editFormData.tipEligible
													}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															tipEligible:
																e.target
																	.checked,
														})
													}
												/>
												<span>Tip Eligible</span>
											</label>
										</div>

										{canViewShiftLeadDayMarks && (
											<label className="timecard-checkbox-row timecard-shift-lead-day-row">
												<input
													type="checkbox"
													checked={
														!!editFormData.is_shift_lead_day
													}
													disabled={
														!canEditShiftLeadDayMarks
													}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															is_shift_lead_day:
																e.target
																	.checked,
														})
													}
												/>
												<span>
													Mark as shift lead this day
													<span className="timecard-shift-lead-day-help">
														{canEditShiftLeadDayMarks
															? "Grants timecard access for this date and location even without a shift-lead schedule role."
															: "You can view this mark but do not have permission to change it."}
													</span>
												</span>
											</label>
										)}

										<div className="timecard-form-grid two-column">
											<div className="timecard-field">
												<label htmlFor="edit-clock-in">
													Clock In{" "}
													<span
														style={{
															color: "#d32f2f",
														}}
													>
														*
													</span>
												</label>
												<input
													id="edit-clock-in"
													type="time"
													value={editFormData.clockIn}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															clockIn:
																e.target.value,
														})
													}
													required
												/>
											</div>
											<div className="timecard-field">
												<label htmlFor="edit-clock-out">
													Clock Out{" "}
													<span
														style={{
															color: "#d32f2f",
														}}
													>
														*
													</span>
												</label>
												<input
													id="edit-clock-out"
													type="time"
													value={
														editFormData.clockOut
													}
													onChange={(e) =>
														setEditFormData({
															...editFormData,
															clockOut:
																e.target.value,
														})
													}
													required
												/>
											</div>
										</div>
									</section>

									<section className="timecard-form-section timecard-breaks-section">
										<div className="timecard-breaks-header">
											<label className="timecard-section-title">
												Breaks
											</label>
											<button
												type="button"
												className="btn btn-secondary btn-sm timecard-add-break-btn"
												onClick={() => {
													setEditFormData({
														...editFormData,
														breaks: [
															...editFormData.breaks,
															{
																startAt: "",
																endAt: "",
																breakTypeId: "",
																name: "",
																isPaid: false,
																expectedDuration: "",
															},
														],
													});
												}}
											>
												Add Break
											</button>
										</div>
										<p className="timecard-break-hint">
											Paid rest breaks use Square&apos;s
											allotted paid time (usually 10
											minutes); extra time is unpaid.
										</p>
										{editFormData.breaks.length === 0 ? (
											<p className="timecard-empty-state">
												No breaks added
											</p>
										) : (
											<div className="timecard-breaks-list">
												{editFormData.breaks.map(
													(breakItem, index) => (
														<div
															key={index}
															className="timecard-break-card"
														>
															<div className="timecard-break-card-header">
																<label className="timecard-break-title">
																	Break{" "}
																	{index + 1}
																</label>
																<button
																	type="button"
																	className="btn btn-secondary btn-sm"
																	onClick={() => {
																		const newBreaks =
																			editFormData.breaks.filter(
																				(
																					_,
																					i,
																				) =>
																					i !==
																					index,
																			);
																		setEditFormData(
																			{
																				...editFormData,
																				breaks: newBreaks,
																			},
																		);
																	}}
																>
																	Remove
																</button>
															</div>
															<div className="timecard-field">
																<label>
																	Break Type{" "}
																	<span
																		style={{
																			color: "#d32f2f",
																		}}
																	>
																		*
																	</span>
																</label>
																<select
																	value={
																		breakItem.breakTypeId ||
																		""
																	}
																	onChange={(
																		e,
																	) => {
																		const newBreaks =
																			[
																				...editFormData.breaks,
																			];
																		newBreaks[
																			index
																		] = {
																			...newBreaks[
																				index
																			],
																			breakTypeId:
																				e
																					.target
																					.value,
																		};
																		const selectedBreakType =
																			breakTypes.find(
																				(
																					bt,
																				) =>
																					bt.id ===
																					e
																						.target
																						.value,
																			);
																		if (
																			selectedBreakType
																		) {
																			newBreaks[
																				index
																			].name =
																				selectedBreakType.name;
																			newBreaks[
																				index
																			].isPaid =
																				selectedBreakType.isPaid;
																			newBreaks[
																				index
																			].expectedDuration =
																				selectedBreakType.expectedDuration ||
																				"";
																		}
																		setEditFormData(
																			{
																				...editFormData,
																				breaks: newBreaks,
																			},
																		);
																	}}
																	required
																	style={{
																		width: "100%",
																		padding:
																			"0.375rem",
																		fontSize:
																			"0.875rem",
																	}}
																>
																	<option value="">
																		Select a
																		break
																		type
																	</option>
																	{locationBreakTypes.map(
																		(
																			bt,
																		) => (
																			<option
																				key={
																					bt.id
																				}
																				value={
																					bt.id
																				}
																			>
																				{formatBreakTypeLabel(
																					bt,
																				)}
																			</option>
																		),
																	)}
																</select>
															</div>
															<div className="timecard-form-grid two-column">
																<div className="timecard-field">
																	<label>
																		Start
																		Time
																	</label>
																	<input
																		type="time"
																		value={
																			breakItem.startAt ||
																			""
																		}
																		onChange={(
																			e,
																		) => {
																			const newBreaks =
																				[
																					...editFormData.breaks,
																				];
																			newBreaks[
																				index
																			] =
																				{
																					...newBreaks[
																						index
																					],
																					startAt:
																						e
																							.target
																							.value,
																				};
																			setEditFormData(
																				{
																					...editFormData,
																					breaks: newBreaks,
																				},
																			);
																		}}
																	/>
																</div>
																<div className="timecard-field">
																	<label>
																		End Time
																	</label>
																	<input
																		type="time"
																		value={
																			breakItem.endAt ||
																			""
																		}
																		onChange={(
																			e,
																		) => {
																			const newBreaks =
																				[
																					...editFormData.breaks,
																				];
																			newBreaks[
																				index
																			] =
																				{
																					...newBreaks[
																						index
																					],
																					endAt: e
																						.target
																						.value,
																				};
																			setEditFormData(
																				{
																					...editFormData,
																					breaks: newBreaks,
																				},
																			);
																		}}
																	/>
																</div>
															</div>
														</div>
													),
												)}
											</div>
										)}
									</section>
								</div>
								<div className="modal-actions timecard-modal-actions">
									{hasPermission("delete_timecards") &&
										selectedTimecard &&
										!isOwnTimecard(selectedTimecard) && (
											<button
												type="button"
												className="btn btn-danger timecard-modal-delete"
												onClick={() => {
													setShowEditModal(false);
													handleDeleteClick(
														selectedTimecard,
													);
												}}
												disabled={editSubmitting}
											>
												Delete
											</button>
										)}
									<button
										type="button"
										className="btn btn-secondary"
										onClick={() => setShowEditModal(false)}
										disabled={editSubmitting}
									>
										Cancel
									</button>
									<button
										type="submit"
										className="btn btn-primary"
										disabled={editSubmitting || loadingJobs}
									>
										{editSubmitting
											? "Updating..."
											: "Update Timecard"}
									</button>
								</div>
							</form>
						</div>
					</div>,
					document.body,
				)}
		</div>
	);
}

export default TimeCards;
